import type { TranslationKeys } from "../types.js";
import { marketing as marketingFr } from "./marketing/fr.js";

export const fr: TranslationKeys = {
  common: {
    loading: "Chargement...",
    error: "Erreur",
    success: "Succès",
    cancel: "Annuler",
    confirm: "Confirmer",
    save: "Sauvegarder",
    delete: "Supprimer",
    edit: "Modifier",
    create: "Créer",
    update: "Mise à jour",
    search: "Recherche",
    filter: "Filtre",
    actions: "Actions",
    back: "Retour",
    next: "Suivant",
    previous: "Précédent",
    close: "Fermer",
    description: "Optimisé pour le référencement avec le rendu côté serveur (SSR) et les composants serveur",
    locale: {
      label: "Langue de l'interface",
      hint: "Copie du tableau de bord et Accept-Language envoyés à l'API.",
      es: "Espagnol",
      en: "Anglais",
      fr: "Français",
      pt: "Portugais",
    },
    theme: {
      label: "Apparence",
      light: "Clair",
      dark: "Sombre",
      system: "Système",
    },
  },
  auth: {
    login: "Se connecter",
    logout: "se déconnecter",
    register: "S'inscrire",
    email: "E-mail",
    password: "Mot de passe",
    forgotPassword: "Mot de passe oublié ?",
    resetPassword: "Réinitialiser le mot de passe",
    rememberMe: "Souviens-toi de moi",
    welcome: "Accueillir",
    welcomeBack: "Content de te revoir",
  },
  email: {
    welcome: {
      subject: "Bienvenue sur notre plateforme !",
      title: "Bonjour {{name}} !",
      content: "Merci d'avoir rejoint notre plateforme. ",
      buttonText: "Commencer",
    },
    verification: {
      subject: "Vérifiez votre email",
      title: "Vérifiez votre email",
      content: "Cliquez sur le lien suivant pour vérifier votre adresse e-mail :",
      buttonText: "Vérifier l'e-mail",
      expiresIn: "Ce lien expirera dans 24 heures.",
    },
    passwordReset: {
      subject: "Réinitialisez votre mot de passe",
      title: "Récupération de mot de passe",
      content: "Cliquez sur le lien suivant pour réinitialiser votre mot de passe :",
      buttonText: "Réinitialiser le mot de passe",
      expiresIn: "Ce lien expirera dans 1 heure.",
      ignore: "Si vous n'avez pas demandé ce changement, veuillez ignorer cet e-mail.",
    },
    houseInvitation: {
      subject: "Vous êtes invité à rejoindre un foyer sur HomeExpense",
      title: "Invitation à la maison",
      greeting: "{{inviterName}} vous a invité à rejoindre « {{houseName}} » sur HomeExpense.",
      content:
        "Créez votre compte ou connectez-vous avec cet e-mail pour accepter l'invitation et commencer à partager les dépenses.",
      buttonRegister: "Créer un compte et rejoindre",
      loginHint: "Vous avez déjà un compte ?",
      loginCta: "Connectez-vous avec ce lien",
      expiresIn: "Cette invitation expire dans 14 jours.",
      footer: "Si vous ne vous attendiez pas à cet e-mail, vous pouvez l'ignorer.",
    },
  },
  errors: {
    generic: "Une erreur s'est produite",
    notFound: "Pas trouvé",
    unauthorized: "Non autorisé",
    forbidden: "Interdit",
    validation: "Erreur de validation",
    serverError: "Erreur de serveur",
    networkError: "Erreur réseau",
  },
  validation: {
    required: "Ce champ est obligatoire",
    email: "E-mail invalide",
    minLength: "Doit contenir au moins {{min}} caractères",
    maxLength: "Ne peut pas dépasser {{max}} caractères",
    passwordMismatch: "Les mots de passe ne correspondent pas",
  },
  dashboardUi: {
    shell: {
      brandTitle: "HomeExpense",
      brandTagline: "Une gestion claire des dépenses du ménage",
      brandSlogan: "Construisez l'avenir de votre famille,\nune dépense à la fois.",
      addExpense: "Ajouter une dépense",
      searchPlaceholder: "Rechercher HomeExpense…",
      userStubName: "Utilisateur",
      userStubRole: "Membre",
      notificationsComingSoon: "Notifications (à venir)",
      noActiveHouse: "Aucune maison sélectionnée",
      openingBalanceLine: "Solde d'ouverture : {{amount}}",
    },
    nav: {
      mainNavigation: "Navigation principale",
      dashboard: "Tableau de bord",
      journal: "Journal",
      search: "Recherche",
      budget: "Budget",
      analytics: "Connaissances",
      vault: "Coffre",
      scan: "Analyse IA",
      financings: "Financement",
      houses: "Maisons",
      profile: "Profil",
      accountData: "Compte et données",
      settings: "Paramètres",
      support: "Soutien",
    },
    placeholder: {
      hint: "Écran en construction (phase UI). ",
    },
    pages: {
      home: "Accueil du tableau de bord",
      login: "Se connecter",
      register: "S'inscrire",
      forgotPassword: "Mot de passe oublié",
      resetPassword: "Réinitialiser le mot de passe",
      settingsPassword: "Changer le mot de passe",
      settingsPasskeys: "Passkeys",
      settingsProfile: "Profil utilisateur",
      settingsPersonal: "Données personnelles",
      settings: "Paramètres",
      support: "Soutien",
      houses: "Mes maisons",
      houseMembers: "Membres de la Chambre",
      expenses: "Journal des dépenses",
      financings: "Financement",
      financingNew: "Nouveau financement",
      financingDetail: "Détails du financement",
      financingEdit: "Modifier le financement",
      expenseNew: "Nouvelle dépense",
      expenseDetail: "Détail des dépenses",
      expensePaidFeedback: "Dépense payée",
      budget: "Budget mensuel",
      vault: "Coffre de garantie",
      vaultNew: "Enregistrer l'actif",
      vaultDetail: "Détail de l'actif",
      scan: "Analyse IA",
      houseAnalytics: "Informations sur les ménages",
      search: "Recherche",
      notifications: "Notifications",
      notificationSettings: "Paramètres de notification",
      settingsDevices: "Appareils et sessions",
      onboarding: "Configuration initiale",
    },
    searchPage: {
      subtitle: "Trouvez les dépenses et les articles de coffre-fort dans votre maison active.",
      backHome: "Retour à la maison",
      noActiveHouse: "Choisissez une maison active pour rechercher son journal et son coffre-fort.",
      minCharsHint: "Tapez au moins 2 caractères pour effectuer la recherche.",
      emptyPrompt:
        "Utilisez le champ ci-dessus pour rechercher des titres de dépenses, des notes ou des catégories, ou des noms et des notes de coffre-fort.",
      resultsFor: "Résultats pour « {{query}} »",
      openJournalWithQuery: "Ouvrir le journal complet",
      noResults: "Aucun match. ",
      sectionExpenses: "Dépenses",
      sectionVault: "Coffre",
    },
    states: {
      loadingHint: "Chargement des données…",
      emptyTitle: "Rien ici encore",
      emptyHint: "Lorsqu'il y a des données, elles apparaîtront ici.",
      errorTitle: "Quelque chose s'est mal passé",
      errorHint: "Réessayez ou vérifiez votre connexion.",
      retry: "Réessayer",
    },
    mobile: {
      openMenu: "Ouvrir le menu",
      homeLink: "Maison",
    },
    analytics: {
      openFromHome: "Afficher les informations sur les dépenses (derniers mois)",
      openFromBudget: "Ouvrir les informations et les graphiques sur les ménages",
      backToBudget: "Retour au budget",
      missingHouse: "Sélectionnez une maison pour voir les analyses.",
      subtitle: "Dépenses payées par rapport au budget prévu par mois, tendances des catégories et conseils budgétaires ({{count}} derniers mois).",
      empty: "Aucune dépense payée pour l'instant pendant cette période. ",
      chartTitle: "Payé ou planifié par mois",
      chartPaid: "Payé",
      chartPlanned: "Prévu",
      chartAvgLine: "Moyenne payée (mois avec dépenses)",
      peakMonthTitle: "Mois où les dépenses sont les plus élevées",
      peakMonthBody: "{{month}} — {{amount}} payé",
      peakMonthNone: "Aucune dépense enregistrée dans cette fenêtre.",
      avgSpendTitle: "Moyenne payée par mois actif",
      avgSpendHint: "Seuls les mois avec des dépenses payées > 0 sont inclus.",
      topCategoriesTitle:
        "Principales catégories par dépenses cumulées ({{count}} derniers mois)",
      otherCategories: "Toutes autres catégories confondues : {{amount}}",
      overspendLeaderTitle:
        "Plus grand dépassement cumulé du budget ({{count}} derniers mois)",
      overspendLeaderBody:
        "« {{name}} » cumule {{amount}} au-dessus du plan sur les mois avec ligne budgétaire, en ne comptant que les {{count}} derniers mois.",
      reviewTitle: "Catégories à revoir",
      reviewHint: "Souvent au-dessus du plan au cours des mois avec une ligne budgétaire : envisagez d'augmenter le plan ou de réduire les dépenses.",
      reviewEmpty: "Aucune catégorie ne correspond à ce modèle dans la fenêtre sélectionnée.",
      reduceTitle: "Possibilité d'abaisser le plan",
      reduceHint: "Souvent en dessous du plan – vous pouvez réduire en toute sécurité l’allocation mensuelle.",
      reduceEmpty: "Aucune catégorie ne correspond à ce modèle dans la fenêtre sélectionnée.",
    },
    onboarding: {
      pageTitle: "Aménagez votre espace en trois étapes",
      progress: "Étape {{current}} de {{total}}",
      stepWelcomeTitle: "Bienvenue sur HomeExpense",
      stepWelcomeBody:
        "Nous vous guiderons pour créer ou confirmer votre logement, inviter des personnes qui partagent les dépenses et fixer un plafond mensuel comme référence. ",
      stepHouseTitle: "Nom de la maison",
      stepHouseBody: "C'est ici que vous regrouperez les dépenses, le budget et les membres.",
      stepHouseHasHouseTitle: "Votre maison",
      stepHouseHasHouseBody:
        'You are already part of “{{name}}”. You can continue or create another home from “Homes” whenever you like.',
      stepMembersTitle: "Qui partage la maison",
      stepMembersBody:
        "Invitez des partenaires ou des colocataires par e-mail. ",
      inviteEmailLabel: "E-mail pour inviter",
      inviteEmailPlaceholder: "nom@exemple.com",
      inviteSend: "Envoyer une invitation",
      inviteSending: "Envoi…",
      inviteSent: "Invitation envoyée.",
      inviteSkipHint: "Vous pourrez inviter plus tard les membres de la Chambre.",
      stepBudgetTitle: "Plafond mensuel (référence)",
      stepBudgetBody:
        "Une limite mensuelle souple permet au tableau de bord d'afficher le rythme des dépenses. ",
      budgetAmountLabel: "Montant mensuel ({{currency}})",
      budgetSkipHint: "Vous pouvez laisser ce champ vide et le configurer plus tard.",
      stepDoneTitle: "Tout est prêt",
      stepDoneBody: "Vous pouvez maintenant utiliser le tableau de bord. ",
      startTour: "Démarrer la visite du panel",
      goHome: "Rentre à la maison",
      skipAll: "Sauter et ouvrir le panneau",
      next: "Suivant",
      back: "Retour",
      creatingHouse: "Création…",
      savingBudget: "Économie…",
      finishing: "Finition…",
      houseNameLabel: "Nom de la maison",
      houseNamePlaceholder: "par ex. ",
      invalidAmount: "Saisissez un montant valide (un nombre supérieur à zéro).",
    },
    productTour: {
      sidebarHomeTitle: "Tableau de bord",
      sidebarHomeDesc: "Revenez ici pour un aperçu de la maison active : solde, dépenses mensuelles et liens rapides.",
      navHousesTitle: "Maisons",
      navHousesDesc:
        "Créez une maison, choisissez celle qui est active et ouvrez les paramètres des membres ou par maison. ",
      navJournalTitle: "Journal",
      navJournalDesc:
        "Liste des dépenses du logement actif : ponctuelles et récurrentes, filtres et détail. ",
      navBudgetTitle: "Budget",
      navBudgetDesc:
        "Plafond mensuel, plan de catégorie et suivi des dépenses réelles. ",
      homeMembersTitle: "Membres du ménage",
      homeMembersDesc:
        "Invitations ouvertes, rôles et répartition par défaut. ",
      sidebarAddExpenseTitle: "Nouvelle dépense",
      sidebarAddExpenseDesc: "Enregistrez une dépense ponctuelle ou ouvrez le formulaire pour l'ajouter au journal.",
      sidebarAddExpenseClosingDesc:
        "Utilisez-le à tout moment pour enregistrer vos dépenses. La visite se termine : vous savez maintenant créer une maison, inviter des personnes, choisir si une dépense est partagée (foyer) ou personnelle, indiquer qui a payé et planifier le budget.",
      housesCreateTourTitle: "Créez votre maison",
      housesCreateTourDesc:
        "Entrez un nom et créez : il contient les dépenses, les membres et le budget. ",
      housesEmptyTourTitle: "Pas encore de maisons",
      housesEmptyTourDesc:
        "Après avoir créé le premier, vous verrez le résumé ici et pourrez ouvrir Membres et Budget. ",
      housesMembersLinkTourTitle: "Membres du ménage",
      housesMembersLinkTourDesc:
        "À partir des membres que vous invitez par e-mail, définissez les rôles et la répartition par défaut des dépenses partagées. ",
      membersInviteTourTitle: "Inviter à la maison",
      membersInviteTourDesc:
        "Entrez l'e-mail et le rôle (membre ou administrateur). ",
      membersSplitTourTitle: "Réparti entre les membres",
      membersSplitTourDesc:
        "Les pourcentages totalisent 100 % et fixent la part de chaque personne sur les dépenses partagées. ",
      budgetHeroTourTitle: "Budget par mois",
      budgetHeroTourDesc:
        "Choisissez le mois avec les flèches ou le sélecteur de mois. ",
      budgetGoalTourTitle: "Objectif et barre de progression",
      budgetGoalTourDesc:
        "La barre compare ce qui est déjà payé ce mois-ci avec le plafond ou la somme prévue par catégorie. ",
      homeQuickActionsTitle: "Actions rapides",
      homeQuickActionsDesc: "Raccourcis pour ajouter une dépense, numériser un reçu ou gérer les membres.",
      settingsReplay: "Afficher à nouveau la visite guidée du panneau",
      settingsReplayHint: "Ouvre la maison avec la présentation guidée de l'interface.",
      fallbackTitle: "Votre tableau de bord",
      fallbackBody:
        "Utilisez le menu latéral (ou mobile) : Maisons pour enregistrer le ménage, Journal et Budget pour travailler, et depuis la maison les liens rapides vers les membres et les dépenses.",
    },
    auth: {
      appName: "HomeExpense",
      loginTitle: "Content de te revoir",
      loginSubtitle: "Entrez vos identifiants pour accéder à votre journal financier.",
      registerTitle: "Créez votre espace",
      registerSubtitle: "Inscrivez-vous pour gérer les dépenses du ménage avec le calme éditorial.",
      forgotTitle: "Récupérer l'accès",
      forgotSubtitle: "Entrez votre email et nous vous enverrons un lien pour réinitialiser votre mot de passe.",
      resetTitle: "Nouveau mot de passe",
      resetSubtitle: "Choisissez un mot de passe fort pour votre compte.",
      name: "Nom",
      namePlaceholder: "Votre nom",
      emailLabel: "E-mail",
      emailPlaceholder: "nom@exemple.com",
      passwordLabel: "Mot de passe",
      passwordPlaceholder: "••••••••",
      confirmPasswordLabel: "Confirmez le mot de passe",
      signInCta: "Se connecter",
      signUpCta: "Créer un compte",
      sendResetLink: "Envoyer le lien",
      saveNewPassword: "Enregistrer le mot de passe",
      createAccountLink: "Créez votre espace",
      registerPrompt: "Vous n'avez pas de compte ?",
      alreadyHaveAccount: "Vous avez déjà un compte ?",
      backToLogin: "Retour pour vous connecter",
      forgotLink: "Mot de passe oublié ?",
      forgotSentHint: "Si un compte existe pour cet e-mail, vous recevrez un lien sous peu.",
      resetSuccess: "Mot de passe mis à jour. ",
      invalidResetToken: "Ce lien n'est pas valide ou a expiré. ",
      signingIn: "Connexion…",
      signingUp: "Création d'un compte…",
      inviteLoginBanner: "Connectez-vous avec l'e-mail d'invitation pour rejoindre le foyer.",
      dividerOr: "ou",
      continueWithGoogle: "Continuer avec Google",
      continueWithApple: "Continuer avec Apple",
      continueWithPasskey: "Continuer avec Passkey",
      signUpWithPasskey: "S'inscrire avec Passkey",
      registerWithPasskeyAfterSignUp: "Enregistrer une passkey après la création du compte",
      continuingWithGoogle: "Connexion avec Google…",
      continuingWithApple: "Connexion avec Apple…",
      continuingWithPasskey: "Connexion avec Passkey…",
      passkeyPreferredHint:
        "Si vous avez déjà enregistré une passkey, elle apparaît en premier ; le navigateur peut la suggérer au toucher.",
      errors: {
        generic: "Quelque chose s'est mal passé. ",
        invalidEmailOrPassword: "Email ou mot de passe invalide.",
        userAlreadyExists: "Un compte avec cette adresse email existe déjà.",
        passwordTooShort: "Le mot de passe est trop court.",
        passwordTooLong: "Le mot de passe est trop long.",
        invalidEmail: "L'e-mail n'est pas valide.",
        invalidToken: "Ce lien n'est pas valide ou a expiré.",
        resetDisabled: "La récupération du mot de passe n'est pas disponible.",
        invalidOrigin: "Origine non autorisée. ",
        network: "Erreur réseau. ",
        emailNotVerified: "Veuillez vérifier votre adresse e-mail avant de vous connecter.",
        invalidPassword: "Le mot de passe actuel est incorrect.",
        credentialAccountNotFound: "Ce compte n'a pas de mot de passe (par exemple, connexion sociale uniquement). ",
        providerNotConfigured: "La connexion à Google n'est pas disponible. ",
      },
    },
    houses: {
      pageTitle: "Mes maisons",
      createTitle: "Nouvelle maison",
      nameLabel: "Nom",
      namePlaceholder: "par ex. ",
      createCta: "Créer une maison",
      creating: "Création…",
      yourRole: "Votre rôle",
      membersLink: "Membres",
      setActive: "Utilisez cette maison",
      emptyTitle: "Vous n'avez pas encore de maisons",
      emptyHint: "Créez une maison pour commencer à suivre les dépenses de votre foyer.",
      cardSummaryPeriod: "Résumé {{yearMonth}}",
      cardPlannedShort: "Prévu",
      cardSpentShort: "Payé",
      cardUsageShort: "Usage",
      cardStatusWithin: "Dans les limites du budget",
      cardStatusTight: "Près de la limite",
      cardStatusOver: "Au-delà du budget",
      cardStatusNeutral: "Pas de plafond ni de forfait mensuel",
      cardSummaryError: "Impossible de charger le résumé",
      openingBalanceLabel: "Solde bancaire d'ouverture",
      openingBalanceEdit: "Modifier le solde d'ouverture",
      openingBalanceSave: "Sauvegarder",
      openingBalanceCancel: "Annuler",
      openingBalanceSaving: "Économie…",
      openingBalanceAmountLabel: "Montant",
      openingBalanceHint: "Solde bancaire de référence lors de l'installation de la maison (unités mineures : centimes). ",
      openingBalanceReadOnlyHint: "Seuls les propriétaires ou les administrateurs peuvent modifier le solde d'ouverture.",
      openingBalanceInvalidAmount: "Entrez un montant valide (0 ou plus).",
      openingBalanceLoadError: "Impossible de charger le solde d'ouverture.",
      financialProfileSectionTitle: "Profil financier",
      financialProfileSubtitle: "Solde d'ouverture de référence, devise du foyer et plafond mensuel facultatif.",
      financialProfileEdit: "Modifier le profil financier",
      financialProfileFinancingsLink: "Voir les financements",
      currencyLabel: "Devise (ISO 4217)",
      currencyPlaceholder: "Code à trois lettres, ex. EUR",
      monthlyCapLabel: "Plafond mensuel (facultatif)",
      monthlyCapPlaceholder: "Ex. 2500,50",
      monthlyCapHint: "Limite de référence du foyer ; laisser vide pour retirer le plafond.",
      monthlyCapNone: "Pas de plafond mensuel",
      financialProfileReadOnlyHint: "Seuls les propriétaires ou administrateurs peuvent modifier le profil financier.",
      financialProfileInvalidCurrency: "La devise doit être un code ISO exactement sur 3 lettres (ex. EUR).",
      monthlyCapInvalidAmount: "Saisissez un montant de plafond valide ou laissez le champ vide.",
      financialProfileSaveSuccess: "Profil financier enregistré.",
      financialProfileLoadError: "Impossible de charger le profil financier.",
      setActiveSuccess: "Résidence active mise à jour.",
      editNameCta: "Modifier le nom",
      editNameSave: "Enregistrer le nom",
      editNameCancel: "Annuler",
      deleteCta: "Supprimer la résidence",
      deleteConfirm:
        "Supprimer cette résidence ? Les données associées seront supprimées. Cette action est irréversible.",
      updateNameSuccess: "Nom de la résidence mis à jour.",
      updateNameError: "Impossible de mettre à jour le nom. Réessayez.",
      deleteSuccess: "Résidence supprimée.",
      deleteError: "Impossible de supprimer la résidence. Réessayez.",
    },
    members: {
      pageTitle: "Membres",
      roleLabel: "Rôle",
      roleAdmin: "Administrateur",
      roleMember: "Membre",
      removeCta: "Retirer",
      removeConfirm: "Supprimer ce membre de la maison ?",
      columnUser: "Utilisateur",
      columnRole: "Rôle",
      columnSplit: "Diviser (%)",
      backToHouses: "Retour aux maisons",
      inviteSectionTitle: "Inviter par email",
      inviteSectionAdminOnly:
        "Seuls les propriétaires et les administrateurs peuvent inviter ou gérer les membres.",
      inviteHelp:
        "Nous vous enverrons par e-mail un lien pour créer un compte ou vous connecter. L'invitation doit être acceptée avec la même adresse e-mail.",
      inviteEmailLabel: "E-mail invité",
      inviteEmailPlaceholder: "nom@exemple.com",
      inviteSendCta: "Envoyer une invitation",
      inviteSending: "Envoi…",
      inviteSent: "Invitation envoyée. ",
      pendingInvitesTitle: "Invitations en attente",
      pendingInviteExpires: "Expire {{date}}",
      pendingInviteExpiredBadge: "Expiré",
      revokeInviteCta: "Révoquer",
      revokeInviteConfirm: "Révoquer cette invitation ?",
      registerInviteHeadline: "Vous êtes invité",
      registerInviteSubline: "Rejoignez « {{houseName}} » — utilisez l'adresse e-mail à laquelle cette invitation a été envoyée.",
      emailLockedForInvite: "Cet email est défini par votre invitation.",
      inviteAcceptWrongEmail: "Connectez-vous avec l'e-mail auquel l'invitation a été envoyée.",
      inviteAcceptedJoined: "Vous avez rejoint le foyer.",
      inviteAcceptedAlreadyMember: "Vous étiez déjà membre ; ",
      removeOwnerNotAllowed: "Le propriétaire ne peut pas être supprimé d'ici.",
      removeMemberSuccess: "Membre retiré de la maison.",
      changeRoleSaved: "Rôle mis à jour.",
      roleOwner: "Propriétaire",
      splitSectionTitle: "Configuration divisée",
      splitSectionHelp:
        "Définissez la part du budget mensuel de chaque membre. ",
      splitBudgetEyebrow: "Budget mensuel",
      splitNoBudgetHint:
        "Sans plafond mensuel, nous ne pouvons pas afficher les montants par personne. ",
      splitOpenBudgetLink: "Aller au budget",
      splitYouBadge: "Toi",
      splitTotalLine: "Total : {{percent}} %",
      splitTotalValid: "Les fractionnements totalisent 100 %",
      splitRemainderHint: "Calculé automatiquement pour atteindre 100%.",
      splitSaveCta: "Enregistrer le partage",
      splitSaving: "Économie…",
      splitSaveSuccess: "Séparation enregistrée.",
      splitReadOnlyHint: "Seuls les administrateurs et les propriétaires peuvent modifier la répartition.",
    },
    home: {
      periodLabel: "Période {{yearMonth}}",
      spentMonth: "Dépensé ce mois-ci",
      totalToContributeLabel: "Total à verser ce mois-ci",
      plannedBudget: "Budget prévu",
      budgetUsage: "Utilisation du budget",
      openingBalance: "Solde de référence",
      upcomingTitle: "Dépenses à venir",
      upcomingEmpty: "Aucune date d'échéance à venir.",
      ledgerRecentEmpty:
        "Aucune dépense pour l'instant pour cette maison. ",
      quickActions: "Actions rapides",
      actionExpense: "Ajouter une dépense",
      actionScan: "Scanner le reçu",
      actionMembers: "Gérer les membres",
      insightsStub: "Des comparaisons et des conseils d’économies apparaîtront ici bientôt.",
      heroOpeningLabel: "Solde bancaire d'ouverture",
      heroProjectedBalanceLabel: "Solde estimé du compte",
      heroProjectedBalanceHint:
        "Base {{opening}} + budget {{need}} − payé {{spent}} − retenu {{retained}}",
      heroStatSpentLabel: "Période de paiement",
      heroStatForecastExpensesLabel: "Dépenses prévues sur la période",
      heroStatPlannedLabel: "Budget prévu",
      insightBadge: "Aperçu intelligent",
      insightTitleWithin: "Vous êtes sur la bonne voie",
      insightTitleTight: "Surveillez le budget",
      insightTitleOver: "Budget dépassé",
      insightTitleNeutral: "Aperçu de la période",
      insightBodyWithin:
        "Vous avez utilisé {{percent}} % du budget prévu ce mois-ci : vous avez la possibilité de vous ajuster sereinement.",
      insightBodyTight: "Une grande partie du budget est déjà utilisée. ",
      insightBodyOver: "Les dépenses dépassent le budget prévu ({{percent}}%). ",
      insightBodyNeutral: "Définissez un plafond mensuel dans le profil financier de la maison pour un suivi automatique.",
      insightCta: "Voir les détails dans le journal",
      ledgerEyebrow: "Le grand livre",
      ledgerTitle: "Mouvements récents",
      ledgerViewAll: "Ouvrir le journal complet",
      budgetProgressSubline: "{{spent}} sur {{cap}} utilisé",
      gridSpentHint: "Ce mois-ci",
      gridForecastHint: "Prévision",
      forecastExpensesMonth: "Engagé (tous les frais)",
      gridBudgetHint: "Plan",
      gridUsageHint: "Usage",
      gridSpentSharedLine: "Foyer (partagé)",
      gridSpentPersonalLine: "Personnel (paiement unique)",
      gridBudgetMonthlyLabel: "Budget mensuel",
      ledgerRowBadge: "Prochain",
      moneySnapshotTitle: "Vue argent du mois",
      moneySnapshotOpening: "Solde initial",
      moneySnapshotNeedMonth: "Besoin ce mois",
      moneySnapshotRetained: "Retenu (proratisé)",
      moneySnapshotAvailableAfterRetained: "Disponible après retenu",
      moneySnapshotProjectedBalance: "Solde estimé du compte",
      budgetSetupUrgentTitle: "Pas encore de référence budgétaire du mois",
      budgetSetupUrgentBody:
        "Sans lignes par catégorie ni plafond mensuel du foyer, le suivi perd en sens. Configurez-le dans Budget.",
      budgetSetupUrgentCta: "Ouvrir le budget",
      budgetSetupCapOnlyTitle: "Plafond mensuel défini — ajoutez des catégories",
      budgetSetupCapOnlyBody:
        "Le plafond oriente le rythme des dépenses. Répartir par catégorie clarifie le contrôle.",
      budgetSetupCapOnlyCta: "Planifier par catégorie",
    },
    expenses: {
      newCta: "Nouvelle dépense",
      paid: "Payé",
      unpaid: "En attente",
      paidBy: "Qui a payé",
      scopeShared: "Foyer",
      scopePersonal: "Personnel",
      category: "Catégorie",
      backToJournal: "Retour au journal",
      backToExpenseDetail: "Retour aux dépenses",
      ledgerMonthTotalLabel: "Payé ce mois-ci (frais réglés uniquement)",
      ledgerMonthForecastTotalLabel: "Prévisions mensuelles (tous frais)",
      ledgerMemberSharesTitle: "Part du montant payé (issu des fractionnements)",
      ledgerMemberSharesForecastTitle: "Part prévisionnelle (tous frais)",
      ledgerNoSplitsInMonth:
        "Aucune dépense ce mois-ci n'est partagée par un membre ; ",
      listForecastVsActual: "Prévisions : {{amount}}",
      actionMarkPaid: "Marc a payé",
      actionSetActualAmount: "Montant réel",
      markPaidConfirmTitle: "Marquer comme payé ?",
      markPaidConfirmBody:
        "La dépense sera considérée comme réglée pour l’avancement de ce mois. ",
      markPaidConfirmCta: "Oui, marque payée",
      actionPaidZero: "Payé à 0 €",
      paidZeroConfirmTitle: "Liquider à zéro",
      paidZeroConfirmBody:
        "Le montant réel sera enregistré à 0 € et la dépense sera marquée comme payée. Utile lorsque vous ne dépensez pas ce mois-ci (p. ex. charge bimensuelle) : pas de sortie de trésorerie ce mois ; le paiement réel apparaîtra au mois où vous payez.",
      paidZeroConfirmCta: "Oui, 0 € et payée",
      actualAmountModalTitle: "Montant des dépenses réelles",
      actualAmountModalHint:
        "La prévision reste comme prévu ; ",
      actualAmountLabel: "Montant réel payé",
      actualAmountClear: "Effacer le montant réel",
      actualAmountSubmit: "Sauvegarder",
      paidFeedbackTitle: "Confirmation",
      paidFeedbackHero: "Dépense marquée comme payée !",
      paidFeedbackSubline: "Votre suivi est à nouveau synchronisé.",
      paidFeedbackAmountLabel: "Montant enregistré",
      paidFeedbackCategoryLabel: "Catégorie",
      paidFeedbackPaidByLabel: "Payé par",
      paidFeedbackDateLabel: "Date",
      paidFeedbackContribution: "{{percent}} % de part",
      paidFeedbackBackHome: "Retour à la maison",
      paidFeedbackViewDetail: "Afficher les détails des dépenses",
      form: {
        titleLabel: "Titre",
        titlePlaceholder: "par ex. ",
        titleOptionalHint: "S'il est vide et qu'une catégorie est sélectionnée, le nom de la catégorie est utilisé.",
        amountLabel: "Montant total",
        amountHint: "Utilisez une virgule ou un point pour les décimales (par exemple 24,90).",
        currencyLabel: "Devise (ISO)",
        dateLabel: "Date",
        categoryLabel: "Catégorie",
        categoryNone: "Aucune catégorie",
        paidByLabel: "Qui a payé",
        paidByDefault: "Vous (utilisateur connecté)",
        paidByJointOption: "Compte joint",
        paidByHint:
          "Définit la source des fonds pour la dépense (fonds personnels, compte conjoint du foyer, ou vous par défaut). Cela ne décide pas si la dépense est partagée : utilisez « Dépense du foyer » pour ça.",
        paymentMethodLabel: "Mode de paiement",
        paymentMethodNone: "Non spécifié",
        paymentMethodCard: "Carte",
        paymentMethodCash: "Espèces",
        paymentMethodAccount: "Compte bancaire",
        sharedLabel: "Dépense du foyer (répartie entre les membres)",
        sharedHint:
          "Lorsqu'il est activé, le partage totalise 100 % pour tous les membres du foyer. S'il est désactivé, ce sera une dépense personnelle (non partagée).",
        recurringQuestion: "Est-ce une dépense récurrente ?",
        recurringEndsLabel: "Date de fin",
        recurringEndsOptionalHelp:
          "Facultatif lorsque le prorata est désactivé : laisser vide pour une série ouverte (utilitaires, etc.).",
        recurringEndsRequiredWhenProratedHelp:
          "Avec une fréquence mensuelle et un prorata, une date de fin est requise (répartir un total sur N versements).",
        recurringEndsProrateOpenPeriodHelp:
          "Vous pouvez laisser la date vide : le total correspond au montant de chaque période de facturation (par exemple la facture bimensuelle) et les frais mensuels le répartissent sur cette période.",
        recurringFrequencyLabel: "Fréquence",
        frequencyMonthly: "Mensuel",
        frequencyBimonthly: "Tous les deux mois",
        frequencyQuarterly: "Trimestriel",
        frequencySemiannual: "Semestriel",
        frequencyAnnual: "Annuel",
        proratedLabel: "Répartissez le total sur plusieurs versements",
        proratedHint:
          "Lorsqu'il est activé, le total est divisé entre les paiements par fréquence. ",
        recurringSummary: "Versements prévus : {{count}}. ",
        recurringOpenEndedSummary:
          "Pas de date de fin : les versements sont créés environ {{months}} mois à l'avance ; ",
        recurringProratedOpenPeriodPreviewNote:
          "Le total s'applique à chaque période de facturation de {{periodMonths}} mois ; ",
        submitRecurring: "Activer les dépenses récurrentes",
        notesLabel: "Remarques",
        notesPlaceholder: "Facultatif",
        receiptLabel: "Justificatif (optionnel)",
        receiptHelp: "PDF ou image (JPEG, PNG, WebP, GIF). Téléchargement sécurisé vers le stockage.",
        receiptRemove: "Retirer le fichier",
        paidNowLabel: "Enregistrer comme payé maintenant",
        submitCreate: "Économisez des dépenses",
        submitSave: "Enregistrer les modifications",
        saving: "Économie…",
        cancelEdit: "Annuler la modification",
        invalidAmount: "Entrez un montant valide (0 ou plus).",
        titleOrCategoryRequired: "Entrez un titre ou choisissez une catégorie.",
        recurrenceInvalid: "La date de fin doit être postérieure à la date de début et il doit y avoir au moins un frais dans la plage.",
        recurringProratedRequiresEndsOn:
          "Lors du calcul au prorata du total, définissez une date de fin de série (un total ne peut pas être fractionné sans la fin de la période).",
        recurringPreviewNeedAmount:
          "Saisissez le montant total ci-dessus pour voir le calcul des versements (série avec date de fin ou prorata).",
        recurringPreviewNeedAmountMonthlyFixed:
          "Entrez le montant d’un mois ci-dessus pour voir le résumé et la répartition estimée.",
        proratedMonthlyAverageHint: "Moyenne approximative sur la période : {{amount}} / mois",
        recurrenceKindLegend: "Type récurrent",
        recurrenceKindMonthlyFixed:
          "Frais mensuels fixes (même montant chaque mois ; aucune date de fin requise)",
        recurrenceKindSeries:
          "Séries par fréquence (mensuelle, bimensuelle…) ; ",
        seriesSingleChargeHint:
          "Pour une seule charge par période (annuelle, trimestrielle, semestrielle ou bimestrielle), utilisez Série par fréquence avec prorata désactivé.",
        seriesAnnualPresetAction:
          "Utiliser le preset d’abonnement annuel (sans prorata)",
        amountLabelMonthly: "Montant par mois",
        amountHintMonthlyFixed:
          "Entrez ce que vous payez en un seul mois (par exemple, une hypothèque). ",
        monthlyFixedPatternExplain:
          "Aucune date de fin n'est requise : le journal reçoit {{count}} frais, un par mois, chacun pour le même montant « un mois » que vous avez saisi ci-dessus (par exemple, hypothèque mensuelle). ",
        monthlyFixedHorizonNote:
          "Vous verrez {{count}} lignes de journal (une par mois) ; ",
        smoothBimonthlyBillHint:
          "Si une facture arrive tous les deux mois (par exemple eau ou poubelles) et que vous souhaitez en mettre de côté la moitié chaque mois, utilisez ce mode et saisissez la moitié du montant bimensuel comme « un mois ».",
        sharedSplitPreviewTitle: "Répartition estimée (cette période)",
        sharedSplitPreviewSubtitle:
          "Montant par personne issu de la répartition du ménage. ",
        sharedSplitUsesHousePercent: "Utiliser les pourcentages des membres",
        sharedSplitEqualParts: "Parts égales entre les membres",
      },
      detail: {
        edit: "Modifier",
        markPaid: "Marquer comme payé",
        markUnpaid: "Marquer comme en attente",
        delete: "Supprimer",
        deleteConfirm: "Supprimer cette dépense ? ",
        paidAtLabel: "Payé le",
        notesTitle: "Remarques",
        splitsTitle: "Diviser",
        splitsSubtitle:
          "Montant pour chaque personne basé sur les pourcentages stockés sur cette dépense.",
        splitShare: "{{share}} %",
        notApplicable: "—",
        scopeLabel: "Portée",
        paymentMethod: "Mode de paiement",
        receiptLabel: "Justificatif",
        receiptView: "Voir le justificatif",
        receiptNotAttached: "Aucun justificatif joint",
        receiptReplaceHelp:
          "Vous pouvez téléverser un nouveau justificatif pour remplacer l’actuel sans quitter cet écran.",
        seriesLink: "Voir les séries récurrentes",
        seriesFutureInstallmentLabel:
          "Montant par versement (mise à jour des frais futurs impayés)",
        seriesSave: "Enregistrer les modifications de la série",
        seriesLimitSectionTitle: "Raccourcir ou terminer",
        seriesLimitSectionIntro:
          "Définissez la date de la dernière recharge ou terminez la série maintenant. ",
        seriesEndsOnLabel: "Dernier prélèvement (date de fin)",
        seriesEndsOnHelp:
          "Les échéances impayées postérieures à cette date seront supprimées. ",
        seriesApplyEndsOn: "Enregistrer la date de fin",
        seriesEndNow: "Terminer la série maintenant",
        seriesEndNowConfirm:
          "Les futurs versements impayés seront supprimés et la série cessera de générer des frais. ",
        seriesExtendSectionTitle: "Prolonger la série",
        seriesExtendIntroActiveWithEnd:
          "Supprimez la date de fin afin que les versements soient à nouveau générés jusqu'à l'horizon du système. ",
        seriesExtendIntroEnded:
          "Réactivez la série et supprimez la date de fin. ",
        seriesRemoveEndsOn: "Supprimer la date de fin",
        seriesRemoveEndsOnConfirm:
          "Supprimer la date de fin ? ",
        seriesReactivateOpenEnded: "Réactiver sans date de fin",
        seriesReactivateOpenEndedConfirm:
          "Réactiver la série et supprimer la date de fin ? ",
        seriesReactivateOnly: "Réactiver la série",
        seriesReactivateOnlyConfirm:
          "Réactiver la série ? ",
        seriesExtendIntroEndedNoEnd:
          "Cette série est terminée et n'a pas de date de fin enregistrée. ",
        seriesStatusBadgeActive: "Actif",
        seriesStatusBadgePaused: "En pause",
        seriesStatusBadgeEnded: "Terminé",
        seriesEndedReadOnly:
          "Cette série est terminée. ",
        seriesInvalidEndDate: "Entrez une date valide (AAAA-MM-JJ).",
        budgetSetupBannerTitle: "Aucune référence budgétaire ce mois-ci",
        budgetSetupBannerBody:
          "Le mois de cette dépense n’a ni plan par catégories ni plafond mensuel du foyer. Configurez-les dans Budget.",
        budgetSetupBannerCta: "Ouvrir le budget",
      },
      filters: {
        title: "Filtres",
        searchPlaceholder: "Rechercher dans la revue…",
        scopeLabel: "Portée",
        scopeAll: "Tous",
        scopeShared: "Foyer",
        scopePersonal: "Personnel",
        categoryAll: "Toutes les catégories",
        paidAll: "Tous",
        paidYes: "Payé uniquement",
        paidNo: "Impayé uniquement",
        apply: "Appliquer",
        clear: "Clair",
      },
      flows: {
        launcherTitle: "Que voulez-vous faire ?",
        launcherSubtitle:
          "Choisissez un parcours selon le type de dépense que vous voulez enregistrer.",
        manualOnlyBanner:
          "Saisissez les informations clés de la dépense : montant, date et mode de paiement.",
        intentQuickTitle: "Enregistrer une dépense ponctuelle",
        intentQuickDescription:
          "Pour un paiement déjà effectué : montant, catégorie et moyen de paiement. Le reste seulement si besoin.",
        intentRecurringTitle: "Charge fixe chaque mois",
        intentRecurringDescription:
          "Une échéance qui se répète (loyer, charges…). Un assistant pas à pas explique ce que vous configurez.",
        intentSeriesTitle: "Série avec dates ou prorata",
        intentSeriesDescription:
          "Échéances avec date de fin, autres fréquences ou répartition d’un total sur plusieurs mois. Récapitulatif avant enregistrement.",
        quickExpandMore: "Plus d’options (libellé, notes, partagé…)",
        repeatLastFlowCta: "Continuer avec : {{flowLabel}}",
        navNext: "Suivant",
        navBack: "Retour",
        navReview: "Voir le récapitulatif",
        editStepA11y: "Modifier l’étape {{step}}",
        monthlyWizard: {
          step1Title: "De quelle dépense s’agit-il ?",
          step1Help: "Titre ou catégorie pour la retrouver chaque mois dans le journal.",
          step2Title: "Combien payez-vous par mois ?",
          step2Help: "Un seul montant mensuel. Nous le répétons à chaque échéance.",
          step3Title: "Quand commence-t-elle ?",
          step3Help: "Date du premier prélèvement ou du jour où elle doit apparaître dans le journal.",
          step4Title: "Comment payez-vous ?",
          step4Help: "Carte, espèces ou compte : vous indiquez la sortie d’argent (saisie manuelle).",
          step5Title: "Est-ce partagé au foyer ?",
          step5Help: "Qui paie et si la dépense est partagée entre les membres.",
          reviewTitle: "Ainsi apparaîtra votre charge fixe",
          reviewHelp: "Vérifiez les informations. Vous pouvez revenir en arrière et modifier une étape.",
        },
        seriesWizard: {
          step1Title: "Libellé et montant",
          step1Help:
            "Nature de la dépense et montant selon le mode (par mois ou total de période, selon vos choix suivants).",
          step2Title: "Fréquence de répétition",
          step2Help: "Mensuel, trimestriel, annuel… La fréquence définit les lignes dans le journal.",
          step3Title: "Début et fin",
          step3Help:
            "Date de début et, si besoin, dernière échéance. Sans date de fin, la série peut rester ouverte selon les règles du formulaire.",
          step4Title: "Prorata",
          step4Help:
            "Si vous répartissez un total sur plusieurs échéances, cela explique la répartition. Pas de banque : vous validez les montants.",
          step5Title: "Répartition entre personnes",
          step5Help: "Partagé et payeur. Un aperçu s’affiche quand montant et dates sont renseignés.",
          reviewTitle: "Récapitulatif de la série",
          reviewHelp: "Contrôlez échéances et dates avant de créer la série.",
        },
      },
    },
    financings: {
      pageTitle: "Financement",
      newCta: "Nouveau financement",
      emptyTitle: "Pas encore de financement",
      emptyHint:
        "Enregistrez les prêts ou les versements fixes avec une date de début de contrat. ",
      stepDataTitle: "Détails",
      stepReviewTitle: "Revoir",
      titleLabel: "Nom",
      lenderLabel: "Prêteur ou institution",
      installmentLabel: "Versement mensuel",
      contractStartLabel: "Début du contrat (échéancier des versements)",
      endsOnLabel: "Date du dernier versement",
      notesLabel: "Remarques",
      docsHint: "Documents optionnels (PDF ou image). ",
      runAiLabel: "Analyser des documents avec l'IA",
      runAiHint:
        "Résume les termes clés à titre d’information uniquement. ",
      generateRecurringLabel: "Générer des dépenses récurrentes mensuelles",
      generateRecurringHint:
        "Crée une série de journaux à partir du premier versement à compter d'aujourd'hui (UTC) jusqu'à la date de fin.",
      reviewCutoff: "Date limite : {{date}} (UTC)",
      reviewFirstPayment: "Premier versement du journal : {{date}}",
      reviewFutureCount: "Versements futurs : {{count}}",
      reviewPreviewDates: "Dates à venir (exemple)",
      reviewWillSeries: "Une série de dépenses récurrentes sera créée",
      reviewWillAi: "Le premier document sera envoyé à AI après la sauvegarde",
      reviewNoSeries: "Aucune dépense automatique ne sera créée",
      backCta: "Retour",
      continueCta: "Continuer à réviser",
      confirmCta: "Confirmer et enregistrer",
      saving: "Économie…",
      aiDisclaimer: "Les résumés AI sont indicatifs et peuvent contenir des erreurs.",
      aiSkipped: "IA non utilisée",
      aiPending: "Résumé de l'IA en attente",
      aiProcessing: "Analyser des documents…",
      aiCompleted: "Résumé de l'IA prêt",
      aiFailed: "Le résumé de l'IA a échoué",
      viewSeriesCta: "Afficher les séries de dépenses",
      listInstallment: "Acompte : {{amount}}",
      listTermProgress: "{{percent}}% du terme",
      listContractEndedBadge: "Terminé",
      listInstallmentsSummary:
        "{{past}} sur {{total}} versements écoulés · {{remaining}} restants",
      listEstimatedRemaining: "Encours estimé : {{amount}}",
      listNoInstallmentSchedule: "Aucun calendrier de versement",
      categoryLabel: "Catégorie",
      categoryNone: "Aucune catégorie",
      openDocument: "Ouvrir le document",
      summaryTitle: "Résumé de l'IA",
      keyTermsTitle: "Termes clés",
      editCta: "Modifier",
      deleteCta: "Supprimer",
      deleteConfirm:
        "Supprimer ce financement ? ",
      saveCta: "Enregistrer les modifications",
      saveSuccess: "Financement mis à jour.",
    },
    budget: {
      heroEyebrow: "Allocation du foyer",
      heroTitle: "Dépense du foyer prévue ce mois-ci",
      heroSubtitle:
        "Total de vos lignes budgétaires du foyer pour cette période. Les dépenses personnelles sont suivies séparément dans le journal.",
      goalLabel: "Progression de l'objectif : {{yearMonth}}",
      percentComplete: "{{percent}}% terminé",
      reservedLabel: "{{amount}} dépensé du foyer",
      targetLabel: "{{amount}} cible",
      categoryGridTitle: "Par catégorie",
      plannedLabel: "Prévu du foyer",
      spentLabel: "Dépense du foyer",
      plannedInputHint: "Montant prévu (unités principales)",
      monthNav: "Mois",
      prevMonth: "Mois précédent",
      nextMonth: "Mois prochain",
      saveCta: "Économisez votre budget",
      saving: "Économie…",
      saveSuccess: "Budget mis à jour.",
      copyFromPreviousConfirmTitle: "Copier depuis le mois précédent ?",
      copyFromPreviousConfirmMessage:
        "Les lignes de budget du mois précédent seront appliquées à ce mois. Vous pourrez les ajuster ensuite.",
      copyFromPreviousCta: "Importer depuis le mois précédent",
      readOnlyHint: "Seuls les propriétaires et les administrateurs peuvent modifier le budget.",
      performanceTitle: "Performance de la période",
      performanceSubtitle: "Budget du foyer prévu par rapport aux dépenses réelles du foyer de ce mois-ci.",
      legendBudgeted: "Prévu du foyer",
      legendSpent: "Dépense du foyer",
      noCategoriesHint:
        "Aucune catégorie pour l'instant. ",
      addCategoryCard: "Ajouter une catégorie",
      addCategoryCardHint: "De nouvelles catégories peuvent être sélectionnées lors de la création d'une dépense.",
      addCategoryFormTitle: "Nouvelle catégorie",
      addCategoryNameLabel: "Nom",
      addCategoryNamePlaceholder: "par ex. ",
      addCategorySubmit: "Créer",
      addCategoryCancel: "Annuler",
      addCategoryCreating: "Création…",
      heroVsPrevUp: "+{{pct}}% par rapport au mois dernier",
      heroVsPrevDown: "−{{pct}}% par rapport au mois dernier",
      heroVsPrevFlat: "Comme le mois dernier",
      proratedTitle: "Obligations au prorata",
      proratedLinkJournal: "Afficher le journal",
      proratedReserveHint: "Versement mensuel estimé",
      proratedTotalBadge: "Total {{amount}}",
      proratedEmpty:
        "Les séries avec un total au prorata (taxe foncière, assurance annuelle, etc.) s'afficheront ici. ",
      planningSectionTitle: "Planification des catégories",
      bannerEyebrow: "Habitudes durables",
      bannerTitle: "Un budget clair réduit les surprises et aligne votre foyer sur ce qui compte.",
      badgeOverBudget: "Au-dessus du plan",
      badgeUnderBudget: "Sous plan",
      badgeOnTrack: "En bonne voie",
      badgeNoPlan: "Aucun plan",
      columnBudget: "Budget",
      columnReal: "Réel",
      analysisEmpty:
        "Aucune catégorie avec un plan ou des dépenses pour ce mois-ci pour l'instant. ",
      monthlyCapHint: "Plafond mensuel des ménages",
      moneySnapshotTitle: "Vue rapide de trésorerie",
      moneySnapshotOpening: "Solde initial",
      moneySnapshotNeedMonth: "Besoin ce mois",
      moneySnapshotRetained: "Retenu (proratisé)",
      moneySnapshotAvailableAfterRetained: "Disponible après retenu",
      moneySnapshotProjectedBalance: "Solde estimé du compte",
    },
    vault: {
      pageTitle: "Coffre-fort",
      pageSubtitle:
        "Gérez en toute sécurité les garanties et les politiques résidentielles. ",
      emptyHint: "Enregistrez les garanties, les politiques et les actifs avec les dates clés.",
      addCta: "Ajouter un actif",
      nameLabel: "Nom de l'article",
      namePlaceholder: "par ex. ",
      assetTypeLabel: "Catégorie",
      assetTypePlaceholder: "par ex. ",
      purchaseDateLabel: "Date d'achat",
      warrantyLabel: "Fin de la garantie",
      notesLabel: "Remarques",
      notesPlaceholder: "Détails du contrat, références…",
      notesOptionalHint: "Facultatif",
      documentKeyLabel: "Clé du document (stockage)",
      documentKeyPlaceholder: "Après avoir téléchargé un fichier vers le stockage",
      saveCta: "Sauvegarder",
      saving: "Économie…",
      createCta: "Enregistrer dans le coffre-fort",
      backToList: "Retour au coffre-fort",
      deleteCta: "Supprimer",
      deleteConfirm: "Supprimer cet élément du coffre-fort ?",
      readOnlyHint: "Seuls les propriétaires et les administrateurs peuvent créer ou modifier des éléments.",
      cardWarranty: "Garantie jusqu'au {{date}}",
      cardPurchased: "Acheté {{date}}",
      formKicker: "Inscription",
      formTitle: "Nouvel actif du coffre-fort",
      formSubtitle: "Protégez votre achat avec les détails originaux et une preuve d'achat.",
      priceLabel: "Prix/valeur",
      pricePlaceholder: "0,00",
      priceInNotesLine: "Valeur estimée : {{amount}}",
      documentUploadTitle: "Reçu / facture",
      documentUploadHint: "Télécharger une preuve d'achat",
      documentUploadSub: "Appuyez pour choisir un fichier ou une image",
      documentUploadedHint: "Document joint",
      completionLabel: "Progression de l'inscription",
      saveLegalNote:
        "En enregistrant, vous autorisez le stockage cloud sécurisé de vos documents d'achat et des dates de garantie.",
      filterAll: "Tous",
      filterEmpty: "Aucun article dans cette catégorie.",
      alertKicker: "Alerte",
      alertTitle: "Attention à la garantie",
      alertDaysLeft: "La garantie « {{name}} » expire dans {{days}} jours.",
      alertExpired: "La garantie pour « {{name}} » a expiré. ",
      alertViewDetails: "Afficher les détails",
      statTotalLabel: "Actifs du coffre-fort",
      statSecuredLabel: "Avec date de garantie",
      statSecuredLine: "Articles avec une date de fin de garantie actuelle ou future",
      sectionCount: "{{count}} éléments",
      cardPurchaseDate: "Date d'achat",
      cardExpiryDate: "Fin de la garantie",
      viewDocument: "Afficher le reçu",
      registerNewItem: "Enregistrer un nouvel article",
      warrantyBadgeExpired: "Expiré",
      warrantyBadgeUrgent: "Urgent",
      warrantyBadgeWarning: "Bientôt",
      warrantyBadgeSafe: "D'ACCORD",
      warrantyBadgeNone: "Pas de date",
      warrantyEndsToday: "Se termine aujourd'hui",
      categoryPlaceholder: "Choisir la catégorie",
      sectionUncategorized: "Non classé",
      categories: {
        appliances: "Appareils électroménagers",
        electronics: "Électronique",
        furniture: "Meubles",
        vehicles: "Véhicules",
        other: "Autre",
      },
    },
    scan: {
      overlayTitle: "Analyse IA",
      overlaySubtitle: "Téléchargez une photo du reçu ; ",
      pickFile: "Choisir une image",
      uploading: "Téléchargement…",
      registering: "Enregistrement de la numérisation…",
      statusPending: "En file d'attente",
      statusProcessing: "Traitement",
      statusCompleted: "Complété",
      statusFailed: "Échoué",
      extractedTitle: "Données détectées",
      rawPayloadHint: "Aperçu du résultat brut (JSON).",
      recentTitle: "Analyses récentes",
      linkExpenseHint: "Examinez les données extraites et créez la dépense dans le journal une fois l'analyse terminée.",
      pollHint: "Statut actualisé…",
      createExpenseFromScan: "Créer des dépenses avec ces données",
      fromScanBanner: "Champs pré-remplis à partir d’une analyse AI. ",
      extractionErrorFallback: "Impossible d'interpréter le reçu.",
      extractedMerchant: "Marchand",
      extractedDate: "Date",
      extractedTotal: "Total",
    },
    profile: {
      pageTitle: "Profil",
      accountSettings: "Compte et données",
      accountSettingsHint: "Préférences personnelles",
      notifications: "Notifications",
      notificationsHint: "Push, préférences et appareils",
      devices: "Appareils et sessions",
      devicesHint: "Voyez où vous êtes connecté et déconnectez-vous à distance",
      privacy: "Confidentialité et sécurité",
      privacyHint: "Mot de passe et autorisations",
      help: "Aide et assistance",
      helpHint: "FAQ et contact",
      logout: "Se déconnecter",
      logoutHint: "Déconnectez-vous en toute sécurité",
      footerNote: "HomeExpense",
      profileFormSectionTitle: "Profil et langue",
      nameField: "Nom d'affichage",
      saveButton: "Enregistrer les modifications",
      saving: "Économie…",
      savedHint: "Modifications enregistrées.",
      clearAvatarButton: "Supprimer l'image",
      avatarChangeAriaLabel: "Changer la photo de profil",
      avatarUploading: "Téléchargement de l'image…",
      avatarUploadError: "Impossible de télécharger l'image. ",
    },
    personalDataPage: {
      subtitle: "E-mail, langue de l'interface et nom d'affichage.",
      emailLabel: "E-mail",
      emailHint: "E-mail lié à votre compte. Il ne peut pas être modifié ici.",
      deleteAccountSectionTitle: "Supprimer le compte",
      deleteAccountSectionHint:
        "Supprime définitivement votre utilisateur, vos sessions et vos données personnelles. Dans les foyers partagés, un autre membre apparaîtra comme créateur des enregistrements conservés.",
      deleteAccountButton: "Supprimer mon compte et mes données",
      deleteAccountModalTitle: "Supprimer le compte définitivement ?",
      deleteAccountModalBody:
        "Cette action est irréversible. Vous perdrez l'accès à HomeExpense et vos données de compte seront supprimées. Si vous partagiez un foyer, les autres membres le conservent et l'historique est réattribué.",
      deleteAccountModalConfirm: "Oui, supprimer mon compte",
      deleteAccountModalCancel: "Annuler",
      deleteAccountError: "La suppression n'a pas pu être effectuée. Réessayez ou contactez le support.",
      deleteAccountPending: "Suppression du compte…",
    },
    settingsHub: {
      unifiedPageTitle: "Compte et données",
      unifiedSubtitle: "Photo de profil et liens vers des informations personnelles, la sécurité et l'aide.",
      backToAccount: "Retour au compte et aux données",
      cardPersonalDataTitle: "Données personnelles",
      cardPersonalDataHint: "Langue, nom d'affichage et adresse e-mail",
      cardPasswordTitle: "Mot de passe",
      cardPasswordHint: "Changer le mot de passe lorsque vous êtes connecté",
      cardPasskeysTitle: "Passkeys",
      cardPasskeysHint: "Ajouter ou gérer une connexion sans mot de passe sur ce navigateur",
      cardNotificationsTitle: "Notifications",
      cardNotificationsHint: "Types d’alertes et de push d’appareil",
      cardDevicesTitle: "Appareils et sessions",
      cardDevicesHint: "Sessions actives et déconnexion à distance",
      cardSupportTitle: "Aide et assistance",
      cardSupportHint: "Récupérer l'accès et les FAQ",
      cardDeleteAccountTitle: "Supprimer le compte",
      cardDeleteAccountHint: "Supprimer définitivement votre utilisateur et les données associées",
    },
    settingsPasswordForm: {
      subtitle: "Entrez votre mot de passe actuel et un nouveau. ",
      currentPassword: "Mot de passe actuel",
      newPassword: "Nouveau mot de passe",
      confirmPassword: "Confirmer le nouveau mot de passe",
      submit: "Mettre à jour le mot de passe",
      successMessage: "Mot de passe mis à jour avec succès.",
      forgotPasswordCta: "J'ai oublié mon mot de passe",
      noCredentialHint: "Aucun mot de passe n'est défini pour ce compte. ",
      changeAgain: "Changer encore",
    },
    settingsPasskeysPage: {
      subtitle: "Enregistrez une passkey sur cet appareil pour vous connecter plus vite la prochaine fois.",
      disabledHint: "Les passkeys sont désactivées dans cet environnement (VITE_PASSKEY_AUTH_ENABLED).",
      addHint: "Utilisez un authentificateur intégré (Touch ID, Windows Hello, etc.).",
      addButton: "Ajouter une passkey",
      adding: "Enregistrement…",
      loading: "Chargement des passkeys…",
      loadError: "Impossible de charger les passkeys. Réessayez.",
      empty: "Vous n’avez pas encore de passkey enregistrée pour ce compte.",
      unnamedPasskey: "Passkey sans nom",
      deviceTypeLabel: "Type : {{type}}",
      createdLabel: "Ajout :",
      renameButton: "Renommer",
      renameDialogTitle: "Nom de la passkey",
      renameLabel: "Nom affiché",
      renameSave: "Enregistrer",
      renameEmpty: "Indiquez un nom pour la passkey.",
      deleteButton: "Supprimer",
      deleteConfirm: "Supprimer cette passkey ? Vous ne pourrez plus l’utiliser pour vous connecter.",
      deleting: "Suppression…",
    },
    supportPage: {
      subtitle: "Ressources et liens utiles.",
      intro: "Aide pour l'accès au compte et les liens liés à la sécurité.",
      forgotTitle: "Vous avez oublié votre mot de passe ?",
      forgotBody: "Nous vous envoyons par e-mail un lien pour le réinitialiser sans connaître l'ancien mot de passe.",
      forgotCta: "Allez récupérer l'accès",
    },
    notificationsPage: {
      subtitle: "Alertes et mises à jour pour votre maison.",
      emptyTitle: "Aucune notification",
      emptyHint: "Lorsqu'il y aura des mises à jour (invitations, rappels, etc.), elles apparaîtront ici.",
      inboxTitle: "Boîte de réception",
      pushSectionTitle: "Notifications poussées",
      activatePush: "Activer sur ce navigateur",
      pushWorking: "Le push est actif sur cet appareil.",
      permissionDenied: "Autorisation de notification refusée. ",
      notSupported: "Ce navigateur ne prend pas en charge le Web Push.",
      configMissing: "Firebase n'est pas configuré (vars d'environnement VITE_FIREBASE_* manquantes).",
      registerError: "Impossible d'enregistrer cet appareil pour le push.",
      devicesSectionTitle: "Appareils enregistrés",
      noDevices: "Aucun appareil pour l'instant. ",
      platformLabel: "Plate-forme",
      platformWeb: "Internet",
      platformIos: "IOS",
      platformAndroid: "Androïde",
      lastSeenLabel: "Dernière activité",
      removeDevice: "Supprimer l'appareil",
      prefsSectionTitle: "Préférences de notification",
      prefPushMaster: "Recevoir des notifications push",
      prefHouseInvites: "Accueil et invitations",
      prefExpenses: "Dépenses et journal",
      prefBudget: "Budget et limites",
      prefReminders: "Rappels",
      prefScanDocs: "Numérisations et documents",
      prefDigests: "Résumés",
      prefUpcomingCharges: "Frais à payer demain (impayés)",
      markAllRead: "Marquer tout comme lu",
      markOneRead: "Marquer comme lu",
      loadMore: "Charger plus",
      unreadLabel: "Non lu",
      viewAction: "Ouvrir",
      removeDeviceConfirm: "Supprimer cet appareil des notifications push ?",
      devicePushLabel: "Appuyez sur cet appareil",
    },
    pendingInvitationsPage: {
      title: "Invitations en attente",
      subtitle:
        "Acceptez les invitations à rejoindre des maisons ici. ",
      emptyTitle: "Aucune invitation en attente",
      emptyHint:
        "Si vous avez été invité par e-mail, vérifiez le lien dans le message. ",
      inviterLine: "Invité par : {{inviterName}}",
      expiresLabel: "Expire le {{date}}",
      acceptCta: "Accepter et rejoindre",
      accepting: "Adhésion…",
      loadError: "Impossible de charger les invitations. ",
    },
    notificationSettingsPage: {
      subtitle:
        "Activez les notifications du navigateur, choisissez les alertes que vous recevez et gérez les appareils enregistrés.",
    },
    notificationsInApp: {
      pushDeviceRegisteredTitle: "Dispositif de notification",
      pushDeviceRegisteredBody: "Cet appareil ({{platform}}) a été enregistré avec succès.",
      receiptScanCompletedTitle: "Reçu prêt à être examiné",
      receiptScanCompletedBody:
        "L'analyse de {{houseName}} est terminée. ",
      receiptScanFailedTitle: "Impossible de lire le reçu",
      receiptScanFailedBody:
        "L'analyse de {{houseName}} a échoué. ",
      houseInvitationReceivedTitle: "Invitation à la maison",
      houseInvitationReceivedBody:
        "{{inviterName}} vous a invité à rejoindre «{{houseName}}». ",
      houseInvitationAcceptedTitle: "Quelqu'un a rejoint la maison",
      houseInvitationAcceptedBody:
        "{{inviteeName}} a accepté l'invitation et fait désormais partie de «{{houseName}}».",
      budgetThreshold80Title: "Budget à {{percent}} %",
      budgetThreshold80Body:
        "Dans «{{houseName}}», les dépenses payées pour {{yearMonth}} ont atteint {{percent}}% de l'objectif.",
      budgetThreshold100Title: "Budget à la limite",
      budgetThreshold100Body:
        "Dans «{{houseName}}», les dépenses payées pour {{yearMonth}} ont atteint {{percent}}% de l'objectif.",
      budgetMonthStartedTitle: "Nouveau mois en {{houseName}}",
      budgetMonthStartedBody: "{{yearMonth}} a commencé. ",
      budgetMonthNeedsSetupTitle: "Budget en attente dans {{houseName}}",
      budgetMonthNeedsSetupBody:
        "{{yearMonth}} a commencé et aucun plan précédent n’a pu être copié. Définissez des lignes par catégorie dans Budget ou un plafond mensuel du foyer en secours.",
      budgetMonthSeededFromPreviousTitle: "Budget préparé dans {{houseName}}",
      budgetMonthSeededFromPreviousBody:
        "{{yearMonth}} : le budget par catégories est déjà enregistré pour ce foyer ({{copiedCount}} lignes copiées depuis {{sourceYearMonth}}). Ouvrez Budget uniquement pour vérifier ou ajuster les montants si besoin.",
      vaultWarrantyExpiring7dTitle: "La garantie se termine bientôt",
      vaultWarrantyExpiring7dBody:
        "«{{itemName}}» dans «{{houseName}}» a une garantie jusqu'au {{endDate}} (environ 7 jours restants).",
      upcomingExpenseTomorrowTitle: "Frais à payer demain dans {{houseName}}",
      upcomingExpenseTomorrowBody:
        "Vous avez {{count}} dépenses impayées en date du {{tomorrowDate}}. ",
    },
    devicesPage: {
      subtitle: "Contrôlez où vous êtes connecté.",
      intro:
        "Chaque ligne est une session active. ",
      thisDevice: "Cet appareil",
      currentBadge: "Actuel",
      lastActive: "Dernière activité",
      sessionStarted: "Connecté",
      expires: "Expire",
      ip: "IP",
      browser: "Navigateur ou application",
      unknownBrowser: "Inconnu",
      signOutDevice: "Se déconnecter sur cet appareil",
      signOutDeviceConfirm:
        "Se déconnecter sur cet appareil ? ",
      signOutOthers: "Déconnectez-vous partout ailleurs",
      signOutOthersConfirm:
        "Se déconnecter sur tous les autres appareils ? ",
      loadError: "Impossible de charger les sessions.",
      actionError: "L'action n'a pas pu être complétée. ",
      empty: "Aucune session active.",
    },
  },
  mobile: {
    common: {
      cancel: "Annuler",
      confirm: "Confirmer",
      skip: "Sauter",
      ok: "D'ACCORD",
      changesSaved: "Modifications enregistrées.",
      retry: "Réessayer",
      loadError: "Impossible de charger les données.",
      signingOut: "Déconnexion…",
      signOutConfirmTitle: "se déconnecter",
      signOutConfirmMessage: "Êtes-vous sûr de vouloir vous déconnecter sur cet appareil ?",
      signOutFailedTitle: "Impossible de se déconnecter",
      signOutFailed:
        "Vérifiez votre connexion et réessayez. ",
    },
    session: {
      checking: "Séance de vérification…",
    },
    api: {
      errors: {
        network: "Pas de connexion ou le serveur ne répond pas. ",
        generic: "L'opération n'a pas pu être complétée. ",
        serverUnavailable: "Le service est temporairement indisponible. ",
        missingConfig: "L'URL de l'API n'est pas configurée. ",
      },
    },
    tabs: {
      home: "Maison",
      houses: "Maisons",
      scan: "Balayage",
      budget: "Budget",
      vault: "Coffre",
    },
    shell: {
      comingSoon:
        "Cette rubrique sera bientôt disponible. ",
    },
    home: {
      title: "HomeExpense",
      subtitle: "Votre espace de financement des ménages, avec la clarté du grand livre immobilier.",
      heroEyebrow: "SOLDE BANCAIRE D’OUVERTURE",
      heroTitle: "Bienvenue",
      heroSubtitle: "Gérez les dépenses et le budget avec une orientation éditoriale calme.",
      heroTrend: "Synchronisé avec l'API",
      heroRemainingLabel: "{{amount}} restant estimé",
      openSheet: "Feuille inférieure ouverte",
      openPaginatedDemo: "Notifications (API)",
      healthChecking: "API : vérification…",
      healthOk: "API : en ligne",
      healthFail: "API : inaccessible",
      insightTitle: "À portée de",
      insightBody:
        "Votre rythme de dépenses ce mois-ci est stable. ",
      insightBadge: "Efficacité",
      insightValue: "Excellent",
      insightValueGood: "Excellent",
      insightValueFair: "En bonne voie",
      insightValueOver: "Dépassé",
      upcomingCardBadge: "Prochaine dépense",
      upcomingCardBodyGeneric: "Charge prévue dans cette période.",
      upcomingCardNone: "Aucune dépense en attente",
      budgetCardTitle: "Budget mensuel",
      budgetCardCompleted: "{{percent}}% utilisé",
      budgetCardTotal: "Total : {{amount}}",
      monthSummaryTitle: "Résumé du mois",
      monthSummaryPersonalLabel: "Personnel",
      monthSummarySharedLabel: "Commun",
      monthSummaryTotalLabel: "Total à verser",
      monthSummaryForecastPrefix: "Prévu : {{amount}}",
      houseSectionTitle: "Budget du foyer",
      personalSectionTitle: "Dépenses personnelles",
      quickActionsTitle: "Actions rapides",
      quickExpense: "Consigner les dépenses",
      quickScan: "Scanner le reçu",
      quickMembers: "Membres",
      quickFinancings: "Financements",
      quickFabA11y: "Ouvrir le menu d'actions rapides",
      quickFabCloseA11y: "Fermer le menu d'actions rapides",
      quickFabLabel: "Actes",
      quickFabCloseLabel: "Fermer",
      openJournal: "Journal ouvert",
      openJournalHeaderA11y: "Ouvrir le journal",
      financingsShortcutTitle: "Financements",
      financingsShortcutSubtitle: "Prêts et versements pour votre maison active.",
      analyticsShortcutTitle: "Informations sur les ménages",
      analyticsShortcutSubtitle: "Graphiques pour les signaux payants, planifiés et de catégorie.",
      openingBalanceLabel: "Solde d'ouverture du mois",
      openingBalanceMock: "2 450,00 €",
      defaultHouseName: "Résidence principale",
      notificationsA11y: "Notifications",
      profileA11y: "Profil",
      houseSelectorA11y: "Sélectionnez la maison active",
      noHouseTitle: "Pas encore de maisons",
      noHouseBody:
        "Créez ou rejoignez une maison sur le Web pour synchroniser ici les dépenses, le budget et les données du coffre-fort.",
      chooseHouseTitle: "Maison active",
      recentTitle: "Dépenses à venir",
      upcomingViewAll: "Voir tout",
      goToChangePassword: "Changer le mot de passe",
      budgetSetupUrgentTitle: "Pas encore de référence budgétaire du mois",
      budgetSetupUrgentBody:
        "Sans lignes par catégorie ni plafond mensuel du foyer, la progression est difficile à lire. Configurez dans Budget.",
      budgetSetupUrgentCta: "Ouvrir le budget",
      budgetSetupCapOnlyTitle: "Plafond mensuel défini — ajoutez des catégories",
      budgetSetupCapOnlyBody:
        "Le plafond oriente le rythme des dépenses. Répartir par catégorie clarifie le contrôle.",
      budgetSetupCapOnlyCta: "Planifier par catégorie",
    },
    journal: {
      title: "Journal financier",
      searchPlaceholder: "Rechercher dans la revue…",
      fabA11y: "Ajouter une dépense",
      fabLabel: "Ajouter une dépense",
      filterAll: "Tous",
      filterHousehold: "Foyer",
      filterPersonal: "Personnel",
      filterGroceries: "Épiceries",
      filterUtilities: "Utilitaires",
      filterInsurance: "Assurance",
      groupMonth: "octobre 2025",
      expense1Title: "Boutique hebdomadaire",
      expense2Title: "Électricité",
      expense1Meta: "12 octobre ·",
      expense2Meta: "8 octobre ·",
      expense1Amount: "142,50 €",
      expense2Amount: "89,20 €",
      memberYou: "Toi",
      badgePaid: "Payé",
      badgeUnpaid: "En attente",
      empty: "Aucune dépense ne correspond à votre recherche ou filtre.",
      emptyTitle: "Pas encore de dépenses",
      emptyDescription:
        "Enregistrez vos achats et vos reçus pour consulter votre historique par mois et rester au courant des dépenses du ménage.",
      emptyFilteredTitle: "Aucun résultat",
      emptyCta: "Ajouter une dépense",
    },
    expenseQuickActions: {
      menuA11y: "Plus d'actions pour cette dépense",
      markPaidChip: "Marc a payé",
      adjustActualChip: "Montant réel",
      paidZeroChip: "Payé à 0€",
      paidZeroConfirmTitle: "S'installer à zéro",
      paidZeroConfirmMessage:
        "Le montant réel sera enregistré à 0 et la dépense sera marquée comme payée. ",
      paidZeroSuccess: "Dépense réglée à 0 et marquée comme payée.",
      actualAmountPlaceholder: "0,00",
    },
    expenseDetail: {
      screenTitle: "Détails des dépenses",
      totalAmountLabel: "MONTANT TOTAL",
      dateLabel: "Date",
      categoryLabel: "Catégorie",
      scopeLabel: "Portée",
      paymentMethodLabel: "Mode de paiement",
      notesLabel: "Remarques",
      paidByLabel: "Payé par",
      recurringLabel: "Récurrence",
      proratedLabel: "Mensuel au prorata",
      splitsTitle: "RÉPARTITION DES COÛTS",
      splitPercent: "{{percent}} % de cotisation",
      markAsPaidCta: "Marquer comme payé",
      markAsUnpaidCta: "Marquer comme en attente",
      markingPaid: "Marquage…",
      markingUnpaid: "Perte…",
      markPaidSuccess: "Dépense marquée comme payée.",
      markUnpaidSuccess: "Dépense marquée comme en attente.",
      editCta: "Modifier",
      noCategoryLabel: "Aucune catégorie",
      noNotesLabel: "Aucune note",
      confirmMarkPaid: "Confirmer le paiement",
      confirmMarkPaidMessage: "Marquer cette dépense comme payée ?",
      confirmMarkUnpaid: "Annuler le paiement",
      confirmMarkUnpaidMessage: "Marquer à nouveau cette dépense comme en attente ?",
      errorMarkPaid: "Impossible de marquer comme payé. ",
      errorMarkUnpaid: "Impossible d'annuler le paiement. ",
      cycleLabel: "Faire du vélo",
      projectionLabel: "Projection",
      perMonth: "mo",
      actualCostLabel: "Coût réel",
      realCostLabel: "Montant réel enregistré",
      updateCostTitle: "Mettre à jour le coût réel",
      updateCostCta: "Coût de mise à jour",
      updateCostError: "Impossible de mettre à jour le coût. ",
      updateCostSuccess: "Coût réel mis à jour.",
      receiptLabel: "Justificatif",
      receiptAttached: "Justificatif joint",
      receiptNotAttached: "Aucun justificatif joint",
      receiptViewCta: "Voir le justificatif",
      receiptAttachCta: "Joindre un justificatif",
      receiptReplaceCta: "Remplacer le justificatif",
      receiptSourceTitle: "Joindre un justificatif",
      receiptSourceMessage: "Choisissez comment joindre le justificatif.",
      receiptSourceCamera: "Prendre une photo",
      receiptSourceGallery: "Choisir depuis la galerie",
      receiptSourceDocument: "Choisir un document",
      receiptUpdating: "Téléversement du justificatif…",
      receiptUpdatedSuccess: "Justificatif mis à jour.",
      receiptUpdateError:
        "Impossible de mettre à jour le justificatif. Veuillez réessayer.",
      deleteCta: "Supprimer une dépense",
      deleteConfirmTitle: "Supprimer une dépense",
      deleteConfirmMessage: "Supprimer cette dépense ? ",
      deleteError: "Impossible de supprimer la dépense. ",
      deleteSuccess: "Dépense supprimée.",
      deleting: "Suppression…",
      freqMonthly: "Mensuel",
      freqBimonthly: "Bimensuel",
      freqQuarterly: "Trimestriel",
      freqSemiannual: "Semestriel",
      freqAnnual: "Annuel",
      methodCard: "Carte",
      methodCash: "Espèces",
      methodAccount: "Compte",
      budgetSetupBannerTitle: "Aucune référence budgétaire ce mois-ci",
      budgetSetupBannerBody:
        "Ce mois n’a ni plan par catégories ni plafond mensuel du foyer. Configurez-les dans Budget.",
      budgetSetupBannerCta: "Ouvrir le budget",
    },
    expenseSeries: {
      screenTitle: "Série récurrente",
    },
    houses: {
      screenTitle: "Maisons et membres",
      subtitle: "Choisissez une maison active pour voir les dépenses et le budget associés.",
      managementEyebrow: "Gestion",
      heroDescription:
        "Organisez vos propriétés et gérez avec précision la répartition des dépenses entre les colocataires.",
      sampleHouseName: "Résidence principale",
      activeBadge: "Actif",
      membersCta: "Membres et invitations",
      budgetHint: "Budget mensuel à 68%",
      budgetHintLive: "Progression mensuelle : {{percent}}% · dépensé à ce jour {{spent}}",
      empty: "Vous n'avez pas encore de maison. ",
      emptyTitle: "Pas encore de maisons",
      emptyDescription:
        "Créez votre première maison pour synchroniser les dépenses, le budget et le coffre-fort avec tous les membres du foyer.",
      setActiveHint: "Appuyez sur la carte pour utiliser cette maison dans l'application.",
      membersScreenTitle: "Membres d'accueil",
      membersSubtitle: "Personnes ayant accès à cette maison et leurs rôles.",
      memberRoleOwner: "Propriétaire",
      memberRoleAdmin: "Administrateur",
      memberRoleMember: "Membre",
      memberSince: "Depuis {{date}}",
      invitePendingTitle: "Invitations en attente",
      inviteExpires: "Expire {{date}}",
      inviteExpired: "Expiré",
      inviteEmpty: "Aucune invitation en attente.",
      membersEmpty: "Aucun membre dans cette maison.",
      inviteCta: "Inviter un membre",
      inviteComingSoon: "Les invitations sont gérées depuis le web. ",
      createScreenTitle: "Nouvelle maison",
      createSubtitle: "Donnez un nom à votre maison pour commencer à suivre les dépenses.",
      nameLabel: "Nom de la maison",
      namePlaceholder: "Par ex. ",
      createCta: "Créer une maison",
      creating: "Création…",
      createSuccess: "Accueil créé avec succès.",
      createError: "Impossible de créer la maison. ",
      financialProfileCta: "Profil financier",
      financingsCta: "Financements",
      financialProfileScreenTitle: "Profil financier",
      financialProfileSubtitle: "Solde de référence, devise et plafond budgétaire mensuel.",
      financialProfileFinancingsCta: "Voir les financements immobiliers",
      openingBalanceLabel: "Solde d'ouverture (compte)",
      openingBalancePlaceholder: "0,00",
      currencyLabel: "Monnaie (ISO 4217)",
      currencyPlaceholder: "EUR",
      monthlyBudgetLabel: "Plafond mensuel (facultatif)",
      monthlyBudgetPlaceholder: "0,00",
      saveProfileCta: "Enregistrer le profil",
      savingProfile: "Économie…",
      saveProfileSuccess: "Profil financier mis à jour.",
      saveProfileError: "Impossible d'enregistrer. ",
      invalidAmount: "Entrez un montant valide (0 ou plus).",
      inviteEmailSection: "Inviter par email",
      inviteRoleLabel: "Rôle",
      inviteEmailLabel: "E-mail invité",
      inviteEmailPlaceholder: "nom@exemple.com",
      inviteSendCta: "Envoyer une invitation",
      inviteSending: "Envoi…",
      inviteSentSuccess: "Invitation envoyée. ",
      inviteError: "Impossible d'envoyer l'invitation. ",
      revokeInviteCta: "Révoquer",
      revokeInviteConfirm: "Révoquer cette invitation ? ",
      revokeInviteSuccess: "Invitation révoquée.",
      revokeInviteError: "Impossible de révoquer. ",
      residentsTitle: "Résidents",
      manageAllCta: "Gérer tout",
      splitAllocationTitle: "Allocation fractionnée",
      allocatedBudgetLabel: "Budget alloué",
      perMonthSuffix: "/ mois",
      adjustSplitCta: "Ajuster la répartition",
      soloMemberHint: "Seulement vous sur cette propriété.",
      inviteCompactCta: "Inviter",
      inviteCardTitle: "Inviter un membre",
      inviteCardBody: "Partagez facilement vos dépenses en invitant d’autres résidents chez vous.",
      inviteCardCta: "Accédez aux membres et invitez",
      proTipTitle: "Astuce pour économiser",
      proTipBody:
        "Les ménages dont les répartitions sont alignées sur l'utilisation réelle économisent souvent davantage sur les services publics. ",
      proTipLink: "Consulter le budget du ménage",
      settingsA11y: "Paramètres d'accueil",
      useAsActiveCta: "Utiliser comme maison active",
      setActiveSuccess: "Résidence active mise à jour.",
      removeMemberCta: "Retirer",
      removeMemberConfirm: "Retirer cette personne du foyer ?",
      removeMemberSuccess: "Membre retiré du foyer.",
      removeMemberError: "Impossible de retirer le membre. Réessayez.",
      changeRoleCta: "Changer de rôle",
      changeRoleTitle: "Nouveau rôle",
      roleUpdatedSuccess: "Rôle mis à jour.",
      roleUpdateError: "Impossible de modifier le rôle. Réessayez.",
      inviteAdminOnly: "Seuls les propriétaires et les administrateurs peuvent inviter.",
    },
    budget: {
      screenTitle: "Budget au prorata",
      subtitle: "Dépenses prévues et dépenses réelles pour la maison active.",
      heroMainTitle: "Argent nécessaire",
      heroMainAccent: "ce mois-ci",
      netBalanceLabel: "Solde net estimé",
      netBalanceMock: "624,30 €",
      progressLabel: "Du plafond mensuel utilisé",
      progressPercent: "{{percent}}%",
      rowCategory: "Catégorie",
      rowBudgeted: "Prévu",
      rowActual: "Réel",
      demoUtilitiesBudgeted: "120 €",
      demoUtilitiesActual: "89 €",
      demoInsuranceBudgeted: "85 €",
      demoInsuranceActual: "85 €",
      emptyRows: "Aucun plan budgétaire de catégorie n'existe pour ce mois-ci.",
      emptyPerformanceTitle: "Aucune donnée de performance pour l'instant",
      emptyPerformanceDescription:
        "Définissez les montants prévus par catégorie pour comparer avec ce que vous dépensez réellement.",
      proratedTotalLabel: "Total au prorata",
      moneyRequiredLabel: "Total requis",
      remanentLabel: "Reste {{amount}}",
      proratedInstallmentType: "Tranche",
      proratedInstallmentMonthly: "Mensuel",
      proratedItemTotal: "Total {{amount}}",
      proratedMoveHint: "Montant mensuel réservé aux versements annuels.",
      adjustGoalsCta: "Ajuster les objectifs",
      fabEditBudget: "Modifier le budget",
      fabCreateBudget: "Créer un budget",
      performanceSectionTitle: "Budgétisé vs réel",
      performanceSectionSubtitle: "Budget prévu par rapport aux dépenses réelles.",
      performanceSectionPlanNote:
        "« Plan » est le montant que vous économisez lors de la modification du budget mensuel de la catégorie ; ",
      filtersCta: "Filtres",
      exportPdfCta: "Exporter un PDF",
      exportPdfComingSoon: "L'exportation PDF sera bientôt disponible.",
      editByCategoryTitle: "Planification des catégories",
      editByCategorySubtitle: "Fixez le montant prévu pour chaque catégorie ce mois-ci.",
      createCategoryCta: "Nouvelle catégorie",
      createCategoryTitle: "Créer une catégorie",
      createCategorySubtitle: "Ajoutez une catégorie à utiliser dans les dépenses et les budgets.",
      categoryCreatedSuccess: "Catégorie créée avec succès.",
      deleteCategoryA11y: "Supprimer la catégorie {{name}}",
      deleteCategoryConfirmTitle: "Supprimer la catégorie",
      deleteCategoryConfirmMessage:
        "Êtes-vous sûr de vouloir supprimer « {{name}} » ? ",
      deleteCategoryCta: "Supprimer",
      deleteCategorySuccess: "Catégorie supprimée.",
      deleteCategoryError: "Impossible de supprimer la catégorie. ",
    },
    analytics: {
      screenTitle: "Informations sur les ménages",
      openFromBudget: "Informations et graphiques",
      backToBudget: "Budget",
      subtitle: "{{count}} derniers mois – signaux payants, planifiés et de catégorie.",
      empty: "Aucune dépense payée pendant cette période. ",
      chartTitle: "Par mois",
      legendPaid: "Payé",
      legendPlanned: "Prévu",
      legendAvg: "Moyenne payée",
      chartScrollHint:
        "Faites glisser le graphique sur le côté pour voir chaque mois ; ",
      chartYAxisA11y: "Axe de montant vertical de zéro à {{max}}.",
      peakTitle: "Mois de pointe",
      peakBody: "{{month}} · {{amount}}",
      peakNone: "Aucune donnée",
      avgTitle: "Moy. payé/mois actif",
      avgHint: "Les mois sans dépenses payées sont exclus.",
      topCategories:
        "Plus fortes dépenses cumulées par catégorie ({{count}} derniers mois)",
      otherCategories: "Autres catégories : {{amount}}",
      overspendTitle: "Plus gros dépassement cumulé ({{count}} derniers mois)",
      overspendBody:
        "« {{name}} » : {{amount}} au-dessus du plan sur les mois avec ligne, cumul sur les {{count}} derniers mois.",
      reviewTitle: "Révision (souvent au-delà du plan)",
      reviewHint: "≥80 % des mois budgétisés ont dépassé le plan (avec un historique suffisant).",
      reviewEmpty: "Aucun pour cette fenêtre.",
      reduceTitle: "Plan de finition (souvent ci-dessous)",
      reduceHint: "≥80 % des mois budgétisés étaient conformes au plan.",
      reduceEmpty: "Aucun pour cette fenêtre.",
    },
    vault: {
      screenTitle: "Coffre de garantie",
      subtitle: "Garanties et actifs avec dates d'expiration.",
      searchPlaceholder: "Fouillez le coffre-fort…",
      alertTitle: "Expiration prochaine",
      alertBody: "La garantie du réfrigérateur expire dans 45 jours.",
      alertBodyLive: "La garantie pour « {{name}} » expire bientôt. ",
      itemFridge: "Réfrigérateur · cuisine",
      itemInsurance: "Assurance habitation",
      completionLabel: "Inventaire terminé",
      empty: "Aucun objet dans le coffre-fort de cette maison.",
      emptyTitle: "Votre coffre-fort est vide",
      emptyDescription:
        "Enregistrez les appareils électroménagers, les meubles et les polices avec les dates de garantie. ",
      emptyFilterTitle: "Rien dans cette catégorie",
      emptyFilterDescription:
        "Essayez un autre onglet ou ajoutez un élément qui correspond à cette section.",
      detailScreenTitle: "Coffre · Actif",
      detailBadge: "Enregistrer",
      detailTitle: "Détails de l'actif",
      detailSubtitle: "Informations d'achat et garantie d'origine.",
      detailAssetTypeLabel: "Catégorie",
      detailPurchaseDateLabel: "Date d'achat",
      detailWarrantyEndsLabel: "Fin de la garantie",
      detailNotesLabel: "Remarques",
      detailDocumentLabel: "Reçu",
      detailDocumentPresent: "Document joint",
      detailDocumentMissing: "Pas de reçu",
      detailNoAssetType: "Aucune catégorie",
      detailNoPurchaseDate: "Non spécifié",
      detailNoWarrantyEnds: "Pas d'expiration",
      detailNoNotes: "Aucune note",
      detailEditCta: "Modifier",
      detailWarrantyExpired: "Garantie expirée",
      detailWarrantyActiveDays: "{{days}} jours restants",
      createScreenTitle: "Ajouter au coffre-fort",
      editScreenTitle: "Modifier l'élément",
      saveSuccess: "Actif enregistré dans le coffre-fort.",
      editSuccess: "Actif mis à jour avec succès.",
      saveError: "Impossible d'enregistrer l'élément. ",
      deleteCta: "Supprimer l'élément",
      deleteConfirm: "Supprimer cet élément du coffre-fort ? ",
      deleteSuccess: "Actif supprimé du coffre-fort.",
      deleteError: "Impossible de supprimer. ",
      nameLabel: "Nom de l'article",
      namePlaceholder: "Par ex. ",
      assetTypeLabel: "Catégorie",
      assetTypePlaceholder: "Par ex. ",
      purchaseDateLabel: "Date d'achat",
      warrantyEndsLabel: "Expiration de la garantie",
      notesLabel: "Remarques",
      notesPlaceholder: "Détails du contrat, références…",
      saveCta: "Enregistrer dans le coffre-fort",
      saving: "Économie…",
      heroEyebrow: "Stockage de documents",
      heroTitle: "Coffre-fort",
      heroDescription:
        "Gérez en toute sécurité vos garanties habitation et vos polices d'assurance. ",
      criticalEyebrow: "Alerte critique",
      criticalBodyUrgent:
        "La garantie pour « {{name}} » expire dans {{days}} jours. ",
      criticalBodyExpired: "La garantie pour « {{name}} » a expiré. ",
      criticalCtaDetails: "Afficher les détails",
      criticalCtaExtend: "Prolonger le forfait",
      statTotalLabel: "Actif total",
      statDocsLabel: "Avec reçu",
      filterAll: "Tous les articles",
      sectionCount: "{{count}} éléments",
      cardPurchaseLabel: "Date d'achat",
      cardExpiresLabel: "Date d'expiration",
      viewReceiptCta: "Afficher le reçu",
      registerNewCta: "Enregistrer un nouvel article",
      badgeExpired: "Expiré",
      badgeEndsToday: "Se termine aujourd'hui",
      badgeDaysLeft: "{{count}} jours restants",
      badgeMonthsLeft: "Il reste {{count}} mois",
      badgeYearsLeft: "Il reste {{count}} ans",
      badgeSafe: "Sûr",
      badgeNoDate: "Pas de date",
      formEyebrow: "Inscription",
      formTitle: "Nouveaux détails sur les actifs",
      formSubtitle: "Sécurisez votre investissement en fournissant les informations d'achat originales.",
      uploadSectionLabel: "Preuve de ticket/facture",
      uploadTitle: "Télécharger une preuve d'achat",
      uploadSubtitle: "Choisissez un fichier ou une photo de votre galerie",
      uploadTypesHint: "JPG, PNG, WebP, GIF ou PDF · jusqu'à {{maxMb}} Mo",
      uploadPickFilesCta: "Choisir un fichier",
      uploadPickGalleryCta: "Galerie",
      uploadUploading: "Téléchargement…",
      uploadAttachedHint: "Ci-joint : {{fileName}}",
      uploadRemoveCta: "Retirer",
      uploadRemoveA11y: "Supprimer le document joint",
      uploadErrorMime: "Type de fichier non autorisé. ",
      uploadErrorSize: "Le fichier dépasse la taille maximale ({{maxMb}} Mo).",
      uploadPermissionDenied:
        "Accès à la photothèque refusé. ",
      uploadErrorGeneric: "Impossible de télécharger le fichier. ",
      uploadSuccessReady: "Fichier prêt. ",
      uploadSoon: "Le téléchargement de fichiers sera bientôt disponible.",
      completionStatusLabel: "État d'achèvement du coffre-fort",
      saveDisclaimer:
        "En enregistrant, vous autorisez le cryptage cloud sécurisé de vos documents d'achat et des dates de garantie.",
      moreOptionsA11y: "Plus d'options",
    },
    journalFilters: {
      screenTitle: "Filtres",
      badge: "Affiner la vue",
      title: "Filtres",
      subtitle: "Ajustez les critères pour trouver des dépenses spécifiques.",
      categoriesSection: "Catégories",
      membersSection: "Membres",
      dateRangeSection: "Plage de dates",
      paidStatusSection: "Statut de paiement",
      scopeSection: "Portée",
      fromLabel: "Depuis",
      toLabel: "À",
      paidOnly: "Payé uniquement",
      unpaidOnly: "Impayé uniquement",
      allStatuses: "Tous",
      applyCta: "Appliquer des filtres",
      resetCta: "Réinitialiser",
      applying: "Candidature…",
    },
    profile: {
      screenTitle: "Compte",
      settingsItem: "Paramètres du compte",
      settingsItemSub: "Gérer vos données personnelles",
      notificationsItem: "Notifications",
      notificationsItemSub: "Alertes et mises à jour d'activité",
      securityItem: "Confidentialité et sécurité",
      securityItemSub: "Mot de passe et autorisations",
      helpItem: "Aide et assistance",
      helpItemSub: "FAQ et centre de contact",
      signOutItem: "se déconnecter",
      signOutItemSub: "Déconnectez-vous en toute sécurité",
      deleteAccountItem: "Supprimer le compte",
      deleteAccountItemSub: "Supprimer définitivement vos données et votre accès",
      deleteAccountConfirmTitle: "Supprimer le compte ?",
      deleteAccountConfirmMessage:
        "Votre utilisateur et vos données personnelles seront supprimés définitivement. Cette action est irréversible.",
      deleteAccountConfirmCta: "Supprimer le compte",
      deleteAccountError: "Impossible de supprimer le compte. Réessayez.",
      totalAssetsLabel: "Actif total",
      activeBudgetsLabel: "Mois avec budget",
      comingSoon: "À venir.",
    },
    settings: {
      hubTitle: "Compte",
      hubSubtitle: "Gérez votre profil, votre sécurité et vos préférences d'application.",
      backToAccount: "Retour au compte",
      cardPersonalTitle: "Données personnelles",
      cardPersonalHint: "Nom, e-mail et langue de l'interface.",
      cardPasswordTitle: "Mot de passe",
      cardPasswordHint: "Mettez à jour votre mot de passe de connexion.",
      cardSessionsTitle: "Appareils et sessions",
      cardSessionsHint: "Sessions actives sur d'autres navigateurs ou appareils.",
      cardPasskeysTitle: "Passkeys et biométrie",
      cardPasskeysHint: "Inscription passkey pre-auth et accès biométrique local.",
      cardNotificationsTitle: "Notifications",
      cardNotificationsHint: "Push, alertes et appareils enregistrés.",
      cardSupportTitle: "Aide et support",
      cardSupportHint: "Récupérez les ressources d’accès et d’aide.",
      cardAppearanceTitle: "Apparence",
      cardAppearanceHint: "Clair, sombre ou correspondre aux paramètres du système.",
      avatarChangeA11y: "Changer la photo de profil",
      avatarUploading: "Téléchargement de la photo…",
      clearAvatar: "Supprimer la photo de profil",
      avatarPickError: "Choisissez une image (JPG, PNG, WebP ou GIF).",
      avatarUploadError: "Impossible de mettre à jour votre photo. ",
      footerBrand: "HomeExpense",
      personalTitle: "Données personnelles",
      personalSubtitle: "Votre email ne peut pas être modifié depuis l'application.",
      emailLabel: "E-mail",
      emailHint: "Pour modifier votre adresse e-mail, contactez le support.",
      nameField: "Nom d'affichage",
      profileFormSectionTitle: "Profil et langue",
      saveButton: "Enregistrer les modifications",
      saving: "Économie…",
      savedHint: "Modifications enregistrées.",
      appearanceTitle: "Apparence",
      appearanceSubtitle: "Choisissez l'apparence de l'application sur votre appareil.",
      appearanceLight: "Lumière",
      appearanceDark: "Sombre",
      appearanceSystem: "Système",
      appearanceCurrent: "Choisi",
      sessionsTitle: "Appareils et sessions",
      sessionsSubtitle: "Séances avec accès à votre compte.",
      sessionsIntro:
        "Vous pouvez vous déconnecter d'autres appareils. ",
      sessionsLoadError: "Impossible de charger les sessions.",
      sessionsEmpty: "Aucune autre session trouvée.",
      sessionsSignOutOthers: "Se déconnecter des autres sessions",
      sessionsSignOutDevice: "se déconnecter",
      sessionsThisDevice: "Cet appareil",
      sessionsCurrentBadge: "Actuel",
      sessionsLastActive: "Dernier actif",
      sessionsStarted: "Connecté",
      sessionsExpires: "Expire",
      sessionsIp: "IP",
      sessionsUnknownUa: "Client inconnu",
      sessionsSignOutDeviceConfirm: "Se déconnecter sur cet appareil ?",
      sessionsSignOutOthersConfirm: "Se déconnecter de toutes les sessions sauf celle-ci ?",
      sessionsActionError: "Impossible de terminer l'action. ",
      passkeysTitle: "Passkeys et biométrie",
      passkeysSubtitle:
        "Gérez le déverrouillage rapide sur cet appareil et préparez le contexte pre-auth pour l'inscription passkey.",
      passkeysBiometricTitle: "Vérifier le déverrouillage biométrique",
      passkeysBiometricHint: "Testez Face ID / Touch ID / empreinte avec votre session actuelle.",
      passkeysBiometricSuccess: "Déverrouillage biométrique validé sur cet appareil.",
      passkeysBiometricError: "Impossible de valider la biométrie. ",
      passkeysContextTitle: "Générer le contexte passkey pre-auth",
      passkeysContextHint:
        "Générez un contexte signé temporaire pour poursuivre l'inscription passkey sur des clients compatibles.",
      passkeysContextSuccess: "Contexte passkey pre-auth généré.",
      passkeysContextSuccessHint: "Expire dans {{seconds}} secondes.",
      passkeysContextError: "Impossible de générer le contexte passkey. ",
      notificationsTitle: "Notifications",
      notificationsSubtitle: "Préférences d’alerte et enregistrement push sur cet appareil.",
      pushSectionTitle: "Notifications poussées",
      activatePush: "Activer ou actualiser le push",
      pushWorking: "Les notifications push sont disponibles sur cet appareil.",
      pushConfigMissing: "Firebase n'est pas configuré dans l'application.",
      pushPermissionDenied: "Autorisation de notification refusée. ",
      pushRegisterSuccess: "Appareil enregistré pour les notifications push.",
      pushFailureNoSession: "Connectez-vous pour enregistrer cet appareil pour les notifications.",
      pushFailurePermissionRequest: "Impossible de finaliser la demande d'autorisation de notification.",
      pushFailureIosRemote:
        "Impossible d'activer les notifications à distance sur cet iPhone. ",
      pushFailureToken:
        "Impossible d'obtenir le jeton de notification. ",
      pushRegisterError: "Impossible d'enregistrer l'appareil pour les notifications.",
      prefsSectionTitle: "Préférences",
      prefPushMaster: "Activer les notifications push",
      prefHouseInvites: "Maison et invitations",
      prefExpenses: "Dépenses et paiements",
      prefBudget: "Budget",
      prefReminders: "Rappels",
      prefScanDocs: "Numérisation et documents",
      prefDigests: "Résumés",
      prefUpcomingCharges: "Frais à payer demain (impayés)",
      devicesSectionTitle: "Appareils enregistrés",
      noPushDevices: "Aucun appareil enregistré pour le push.",
      lastSeenLabel: "Vu pour la dernière fois",
      devicePushLabel: "Push activé",
      removeDevice: "Retirer",
      removeDeviceConfirm: "Supprimer cet appareil de la liste ?",
      platformWeb: "Internet",
      platformIos: "IOS",
      platformAndroid: "Androïde",
      supportTitle: "Aide et support",
      supportSubtitle: "Ressources pour récupérer l’accès ou obtenir de l’aide.",
      supportIntro:
        "Si vous ne pouvez pas accéder à votre compte, vous pouvez demander un lien de réinitialisation de mot de passe.",
      supportForgotTitle: "Vous avez oublié votre mot de passe ?",
      supportForgotBody: "Nous enverrons un lien sécurisé vers l’e-mail que vous fournissez.",
      supportForgotCta: "Demander un lien de récupération",
    },
    scan: {
      screenTitle: "Analyse IA",
      subtitle: "Capturez un reçu pour extraire le montant automatiquement.",
      scanningHint: "Cadrez le document dans la zone de numérisation.",
      openGallery: "Galerie",
      manualEntry: "Saisie manuelle",
      recentTitle: "Analyses récentes",
      empty: "Aucun reçu scanné pour cette maison pour l'instant.",
      emptyTitle: "Aucune analyse récente",
      emptyDescription:
        "Téléchargez une photo d'un reçu et nous extrairons automatiquement le montant avec l'IA.",
      emptyCta: "Télécharger le reçu",
      statusPending: "En attente",
      statusProcessing: "Traitement",
      statusCompleted: "Complété",
      statusFailed: "Échoué",
      uploading: "Téléchargement de l'image…",
      registering: "Enregistrement de la numérisation…",
      uploadSuccess: "Reçu reçu. ",
      pollHint: "Analyser le reçu avec l’IA…",
      createExpenseFromScan: "Créer des dépenses avec ces données",
      extractedTitle: "Données détectées",
      extractedMerchant: "Marchand",
      extractedDate: "Date",
      extractedTotal: "Total",
      extractionErrorFallback: "Impossible d'interpréter le reçu.",
      permissionDenied: "Autorisation de la caméra refusée. ",
      permissionBlocked: "Autorisation bloquée. ",
    },
    addExpense: {
      createTitle: "Nouvelle dépense",
      editTitle: "Modifier une dépense",
      saveSuccess: "Dépense enregistrée avec succès.",
      editSuccess: "Dépense mise à jour avec succès.",
      saveError: "Impossible d'économiser la dépense. ",
      invalidAmount: "Entrez un montant valide.",
      invalidCurrency: "Saisissez un code de devise ISO valide (3 lettres, par exemple EUR).",
      titleOrCategoryRequired: "Ajoutez un titre ou sélectionnez une catégorie.",
      titleLabel: "Titre",
      titlePlaceholder: "Par ex. ",
      amountLabel: "Montant",
      dateLabel: "Date",
      categoryLabel: "Catégorie",
      categoryNone: "Aucune catégorie",
      paymentMethodLabel: "Mode de paiement",
      paymentMethodNone: "Non spécifié",
      paymentMethodCard: "Carte",
      paymentMethodCash: "Espèces",
      paymentMethodAccount: "Compte",
      paidByLabel: "Payé par",
      paidByDefault: "Compte ménage",
      paidNowLabel: "Marquer comme payé maintenant",
      notesLabel: "Remarques",
      notesPlaceholder: "Détails ou références…",
      receiptLabel: "Justificatif",
      receiptAttachCta: "Joindre PDF ou image",
      receiptAttachedHint: "Fichier prêt à enregistrer avec la dépense.",
      receiptClearA11y: "Supprimer le justificatif",
      receiptUploading: "Téléchargement…",
      receiptUploadError: "Impossible de téléverser. Réessayez.",
      receiptLinkedExisting: "Un justificatif est déjà enregistré ; vous pouvez le remplacer ou le retirer.",
      isRecurringLabel: "Dépense récurrente",
      recurrenceKindLabel: "Type de récurrence",
      recurrenceMonthlyFixed: "Fixe mensuellement",
      recurrenceSeries: "Série personnalisée",
      frequencyLabel: "Fréquence",
      freqMonthly: "Mensuel",
      freqBimonthly: "Bimensuel",
      freqQuarterly: "Trimestriel",
      freqSemiannual: "Semestriel",
      freqAnnual: "Annuel",
      endsOnLabel: "Date de fin",
      endsOnPlaceholder: "AAAA-MM-JJ (facultatif)",
      proratedLabel: "Montant au prorata",
      monthlyFixedHint: "{{count}} mensualités seront créées automatiquement.",
      submitCreate: "Créer une dépense",
      submitCreateRecurring: "Créer des séries récurrentes",
      submitSave: "Enregistrer les modifications",
      beforeSubmitSummaryTitle: "Résumé avant d'enregistrer",
      summaryConcept: "Titre",
      summaryUnnamed: "(sans titre)",
      summaryTotalAmount: "Montant total",
      summaryInstallmentAmount: "Acompte pour cette période (fractionné)",
      summaryDate: "Date",
      summaryCategory: "Catégorie",
      summaryPaymentMethod: "Mode de paiement",
      summaryPaidBy: "Payé par",
      summarySharedSplit: "Split (ce versement)",
      summaryScheduleTitle: "Frais de journal (aperçu)",
      summaryMoreDates: "… et {{count}} plus",
      enterAmountForSplit: "Entrez un montant valide pour voir la répartition.",
      sharedPreviewRangeInvalid: "Vérifiez les dates de début et de fin de la série.",
      sharedPreviewFinalizeRecurrence: "Remplissez la récurrence (montant et dates) pour voir le fractionnement.",
      flows: {
        launcherTitle: "Que voulez-vous faire ?",
        launcherSubtitle:
          "Choisissez un parcours selon le type de dépense que vous voulez enregistrer.",
        manualOnlyBanner:
          "Saisissez les informations clés de la dépense : montant, date et mode de paiement.",
        intentQuickTitle: "Enregistrer une dépense ponctuelle",
        intentQuickDescription:
          "Pour un paiement déjà effectué : montant, catégorie et moyen de paiement. Le reste seulement si besoin.",
        intentRecurringTitle: "Charge fixe chaque mois",
        intentRecurringDescription:
          "Une échéance qui se répète (loyer, charges…). Un assistant pas à pas explique ce que vous configurez.",
        intentSeriesTitle: "Série avec dates ou prorata",
        intentSeriesDescription:
          "Échéances avec date de fin, autres fréquences ou répartition d’un total sur plusieurs mois. Récapitulatif avant enregistrement.",
        quickExpandMore: "Plus d’options (libellé, notes, partagé…)",
        repeatLastFlowCta: "Continuer avec : {{flowLabel}}",
        navNext: "Suivant",
        navBack: "Retour",
        navReview: "Voir le récapitulatif",
        editStepA11y: "Modifier l’étape {{step}}",
        monthlyWizard: {
          step1Title: "De quelle dépense s’agit-il ?",
          step1Help: "Titre ou catégorie pour la retrouver chaque mois dans le journal.",
          step2Title: "Combien payez-vous par mois ?",
          step2Help: "Un seul montant mensuel. Nous le répétons à chaque échéance.",
          step3Title: "Quand commence-t-elle ?",
          step3Help: "Date du premier prélèvement ou du jour où elle doit apparaître dans le journal.",
          step4Title: "Comment payez-vous ?",
          step4Help: "Carte, espèces ou compte : vous indiquez la sortie d’argent (saisie manuelle).",
          step5Title: "Est-ce partagé au foyer ?",
          step5Help: "Qui paie et si la dépense est partagée entre les membres.",
          reviewTitle: "Ainsi apparaîtra votre charge fixe",
          reviewHelp: "Vérifiez les informations. Vous pouvez revenir en arrière et modifier une étape.",
        },
        seriesWizard: {
          step1Title: "Libellé et montant",
          step1Help:
            "Nature de la dépense et montant selon le mode (par mois ou total de période, selon vos choix suivants).",
          step2Title: "Fréquence de répétition",
          step2Help: "Mensuel, trimestriel, annuel… La fréquence définit les lignes dans le journal.",
          step3Title: "Début et fin",
          step3Help:
            "Date de début et, si besoin, dernière échéance. Sans date de fin, la série peut rester ouverte selon les règles du formulaire.",
          step4Title: "Prorata",
          step4Help:
            "Si vous répartissez un total sur plusieurs échéances, cela explique la répartition. Pas de banque : vous validez les montants.",
          step5Title: "Répartition entre personnes",
          step5Help: "Partagé et payeur. Un aperçu s’affiche quand montant et dates sont renseignés.",
          reviewTitle: "Récapitulatif de la série",
          reviewHelp: "Contrôlez échéances et dates avant de créer la série.",
        },
      },
    },
    notifications: {
      screenTitle: "Notifications",
      empty: "Aucune notification",
      emptyHint: "Les mises à jour et les alertes de la maison apparaîtront ici.",
      markAllRead: "Marquer tout comme lu",
      markAllReadConfirm: "Marquer toutes les notifications comme lues ?",
      markAllReadSuccess: "Toutes les notifications marquées comme lues.",
      markReadError: "Impossible de marquer comme lu.",
    },
    pendingInvitations: {
      screenTitle: "Invitations en attente",
      subtitle: "Maisons dans lesquelles vous avez été invité. ",
      empty: "Aucune invitation en attente",
      emptyHint: "Lorsque quelqu'un vous invite dans une maison, celle-ci apparaîtra ici.",
      inviterLine: "Invité par : {{inviterName}}",
      expiresLabel: "Expire {{date}}",
      acceptCta: "Accepter et rejoindre",
      accepting: "Adhésion…",
      acceptSuccess: "Vous avez rejoint le foyer avec succès.",
      acceptError: "Impossible d'accepter l'invitation. ",
    },
    financings: {
      screenTitle: "Financements",
      empty: "Aucun financement",
      emptyHint: "Suivez les prêts et les versements fixes pour suivre les progrès.",
      detailScreenTitle: "Détail du financement",
      installmentLabel: "Acompte : {{amount}}",
      termProgressLabel: "{{percent}}% du terme",
      contractStartLabel: "Début du contrat",
      endsOnLabel: "Dernier versement",
      remainingLabel: "Restant estimé : {{amount}}",
      openSeriesCta: "Afficher les dépenses récurrentes",
      noSeries: "Pas de dépenses récurrentes liées",
      aiSummaryTitle: "Résumé de l'IA",
      deleteCta: "Supprimer le financement",
      deleteConfirm: "Supprimer ce financement ? ",
      deleteSuccess: "Financement supprimé.",
      deleteError: "Impossible de supprimer. ",
      remainingEstimatedShort: "Restant estimé",
      addDocumentCta: "Ajouter un document",
      removeDocumentA11y: "Supprimer le document",
      docMaxReached: "Maximum de {{max}} documents.",
      docUploadError: "Impossible de télécharger le fichier. ",
      docTooLarge: "Le fichier est trop volumineux.",
      docMimeNotAllowed: "Type de fichier non autorisé.",
    },
    demo: {
      title: "Notifications",
      itemLabel: "Article {{index}}",
      listSubtitle: "GET /notifications (curseur). ",
      loadingMore: "Chargement plus…",
      sheetTitle: "Exemple de feuille inférieure",
      sheetBody:
        "Ce modal utilise @gorhom/bottom-sheet. ",
      sheetCloseHint: "Faites glisser votre doigt vers le bas ou appuyez à l'extérieur pour ignorer.",
      fetchError: "Impossible de charger la liste.",
      needSession: "Pas de séance. ",
      emptyNotifications: "Aucune notification sur cette page.",
      notificationType: "Tapez : {{type}}",
    },
    auth: {
      screenTitle: "Se connecter",
      signInTitle: "Content de te revoir",
      signInSubtitle: "Entrez vos informations d'identification pour accéder à votre grand livre.",
      registerTitle: "Créer un compte",
      registerSubtitle: "Commencez dès aujourd’hui avec une clarté financière à la maison.",
      registerScreenTitle: "Registre",
      forgotScreenTitle: "Récupérer l'accès",
      forgotBadge: "Portail de sécurité",
      forgotTitle: "Récupérez votre accès financier.",
      forgotTitleLead: "Récupérez votre",
      forgotTitleAccent: "accès financier.",
      forgotSubtitle: "Accéder",
      forgotBody:
        "Saisissez l'adresse e-mail de votre compte HomeExpense. ",
      forgotEmailLabel: "Adresse e-mail enregistrée",
      forgotPlaceholderEmail: "par ex. ",
      contactSupport: "Contacter l'assistance",
      contactSupportHint: "Le support intégré à l'application sera bientôt disponible. ",
      forgotDecorLetters: "LE",
      forgotDecorBrand: "Grand livre de succession",
      forgotDecorVersion: "version 4.0.2",
      subtitle: "Utilisez le même compte que sur le web (email et mot de passe).",
      nameLabel: "Nom et prénom",
      namePlaceholder: "Votre nom",
      emailLabel: "Adresse email",
      emailPlaceholder: "nom@exemple.com",
      passwordLabel: "Mot de passe",
      passwordPlaceholder: "••••••••",
      confirmPasswordLabel: "Confirmer",
      signInCta: "Connectez-vous à Ledger",
      registerCta: "Créer un compte gratuit",
      sendRecoveryCta: "Envoyer le lien de récupération",
      signingIn: "Connexion…",
      registering: "Création d'un compte…",
      sendingRecovery: "Envoi…",
      signOut: "se déconnecter",
      goToSignIn: "Se connecter",
      goToRegister: "Créer un compte",
      goToForgot: "Mot de passe oublié ?",
      backToSignIn: "Retour pour vous connecter",
      orContinueWith: "Ou continuez avec",
      orRegisterWithEmail: "Ou inscrivez-vous par email",
      socialGoogle: "Google",
      socialApple: "Pomme",
      signInWithApple: "Connectez-vous avec Apple",
      signUpWithApple: "Inscrivez-vous avec Apple",
      signInWithPasskey: "Se connecter avec Passkey",
      signUpWithPasskey: "S'inscrire avec Passkey",
      continuingWithGoogle: "Connexion avec Google…",
      continuingWithApple: "Connexion avec Apple…",
      continuingWithPasskey: "Connexion avec Passkey…",
      socialApplePending: "La connexion Apple sur mobile sera bientôt disponible.",
      socialPasskey: "Passkey",
      errorGoogleFailed: "Impossible de se connecter avec Google. ",
      errorGoogleCancelled: "La connexion à Google a été annulée.",
      errorGoogleConfigMissing:
        "Google Sign-In n'est pas configuré dans cette application. ",
      errorAppleFailed: "Impossible de se connecter avec Apple. ",
      errorAppleCancelled: "La connexion Apple a été annulée.",
      errorAppleConfigMissing:
        "Apple Sign-In n'est pas configuré dans cette application. Vérifiez APPLE_CLIENT_ID et APPLE_APP_BUNDLE_IDENTIFIER.",
      errorPasskeyFailed: "Impossible de se connecter avec Passkey. ",
      errorPasskeyUnavailable: "Cet appareil ne prend pas en charge Passkey pour ce flux.",
      biometricPromptTitle: "Déverrouiller HomeExpense",
      socialUnavailable: "La connexion sociale sur mobile sera bientôt disponible.",
      termsAgree: "J'accepte le",
      termsLink: "Conditions d'utilisation",
      privacyLink: "politique de confidentialité",
      termsConnector: "et",
      termsRequired: "Vous devez accepter les conditions et la politique de confidentialité pour continuer.",
      alreadyHaveAccount: "Vous avez déjà un compte ?",
      newHere: "Nouveau ici ?",
      sessionActive: "Connecté",
      forgotSentHint:
        "Si un compte existe pour cet e-mail, vous recevrez un lien sous peu.",
      signIn: "Se connecter",
      errorInvalidCredentials: "Email ou mot de passe incorrect.",
      errorGeneric: "Impossible de se connecter. Veuillez réessayer.",
      errorPasswordMismatch: "Les mots de passe ne correspondent pas.",
      errorSignUpFailed: "Impossible de créer le compte. ",
      errorRecoveryFailed: "Impossible d'envoyer le lien. ",
      registerCheckEmail:
        "Vérifiez votre courrier électronique pour vérifier votre compte avant de vous connecter.",
      changePasswordScreenTitle: "Changer le mot de passe",
      changePasswordBadge: "PARAMÈTRES DE SÉCURITÉ",
      changePasswordTitle: "Mettez à jour vos informations d'identification",
      changePasswordSubtitle:
        "Protégez vos données financières avec un mot de passe fort et unique.",
      currentPasswordLabel: "Mot de passe actuel",
      currentPasswordPlaceholder: "••••••••••••",
      newPasswordLabel: "Nouveau mot de passe",
      newPasswordPlaceholder: "••••••••••••",
      confirmNewPasswordLabel: "Confirmer le nouveau mot de passe",
      changePasswordCta: "Mettre à jour le mot de passe",
      changingPassword: "Mise à jour…",
      changePasswordSuccess: "Mot de passe mis à jour",
      changePasswordSuccessHint:
        "Votre mot de passe a été modifié. ",
      errorChangePasswordFailed:
        "Impossible de mettre à jour le mot de passe. ",
      errorChangePasswordWrong:
        "Le mot de passe actuel est incorrect. ",
      passwordStrengthWeak: "Faible",
      passwordStrengthFair: "Équitable",
      passwordStrengthGood: "Bien",
      passwordStrengthStrong: "Fort",
      passwordStrengthLabel: "Niveau de sécurité",
      securityTipsTitle: "Conseils de sécurité",
      securityTip1Title: "Unicité",
      securityTip1Body:
        "Ne réutilisez pas les mots de passe d'autres services financiers.",
      securityTip2Title: "Complexité",
      securityTip2Body:
        "Mélangez des lettres majuscules, des chiffres et des symboles spéciaux ($@!).",
      changePasswordSignOutHint:
        "Changer votre mot de passe vous déconnectera de tous les autres appareils.",
    },
  },
  marketing: marketingFr,
  api: {
    errors: {
      validationFailed: "Les données soumises ne sont pas valides",
      unauthorized: "Non authentifié",
      forbidden: "Vous n'avez pas l'autorisation pour cette action",
      internal: "Erreur interne du serveur",
      fallback: "Mauvaise demande",
      validation: {
        expectedString: "Le champ {{property}} doit être une chaîne",
        invalidUuid: "Le champ {{property}} doit être un UUID valide",
        expectedInt: "Le champ {{property}} doit être un entier",
        expectedBoolean: "Le champ {{property}} doit être un booléen",
        expectedArray: "Le champ {{property}} doit être un tableau",
        expectedObject: "Le champ {{property}} doit être un objet",
        invalidEnum: "Le champ {{property}} a une valeur non valide",
        min: "Le champ {{property}} est inférieur au minimum autorisé",
        max: "Le champ {{property}} dépasse le maximum autorisé",
        pattern: "Le champ {{property}} a un format non valide",
        invalidField: "Le champ {{property}} n'est pas valide",
      },
      houses: {
        nameRequired: "Le nom est requis",
        createFailed: "Impossible de créer le foyer",
        notFound: "Ménage introuvable",
        nameEmpty: "Le nom ne peut pas être vide",
        userIdRequired: "l'ID utilisateur est requis",
        cannotAssignOwnerViaApi: "Le rôle de propriétaire ne peut pas être attribué via ce point de terminaison",
        userAlreadyMember: "L'utilisateur est déjà membre",
        memberNotFound: "Membre introuvable",
        atLeastOneOwner: "Il doit y avoir au moins un propriétaire",
        defaultSplitBpsRange: "defaultSplitBps doit être compris entre 0 et 10 000",
        cannotRemoveSoleOwner: "Impossible de supprimer le seul propriétaire",
        financialProfileNotFound: "Profil financier introuvable",
        currencyIso: "La devise doit être ISO 4217 (3 lettres)",
        invitationEmailInvalid: "E-mail d'invitation invalide",
        invitationNotFound: "Invitation introuvable ou n'est plus valable",
        invitationTokenInvalid: "Lien d'invitation invalide",
        invitationEmailMismatch: "Connectez-vous avec l'adresse e-mail à laquelle l'invitation a été envoyée",
        invitationEmailFailed: "L'invitation n'a pas pu être envoyée. ",
        invitationUserMissing: "Compte utilisateur introuvable",
      },
      access: {
        notMember: "Vous n'êtes pas membre de ce foyer",
        adminRequired: "Rôle d'administrateur ou de propriétaire requis",
        ownerOnly: "Seul le propriétaire peut effectuer cette action",
      },
      expenses: {
        userNotMember: "L'utilisateur {{userId}} n'est pas membre de ce foyer",
        notFound: "Dépense introuvable",
        titleRequired: "Le titre est requis",
        amountMinorInvalid: "montantMineur n'est pas valide",
        categoryInvalidForHouse: "Catégorie invalide pour ce foyer",
        categoryInvalid: "Catégorie invalide",
        currencyIso: "La devise doit être ISO 4217",
        currencyInvalid: "Devise invalide",
        createFailed: "Impossible de créer la dépense",
        titleEmpty: "Le titre ne peut pas être vide",
        splitSumMustBe10000: "La somme des shareBps doit être exactement de 10 000",
        splitDuplicateUser:
          "Les groupes ne peuvent pas inclure le même utilisateur plus d'une fois (ID utilisateur en double dans les groupes)",
        splitShareBpsInvalid: "shareBps invalide (doit être compris entre 0 et 10 000)",
        recurrenceEndsBeforeStart: "La date de fin doit être identique ou postérieure à la date de début",
        recurrenceNoOccurrences: "Aucune date de versement ne se situe dans la plage sélectionnée",
        proratedRequiresEndsOn:
          "Avec une fréquence mensuelle, le calcul au prorata d'un total nécessite une date de fin de série (ou utilisez bimensuel/trimestriel… pour diviser le total de chaque période en lignes mensuelles sans date de fin)",
        seriesNotFound: "Série récurrente introuvable",
        seriesFixedSplitsRequired:
          "Fournir des actions fractionnées pour une série à fractionnement fixe",
        seriesSplitsOnlyForFixed:
          "Les splits ne peuvent être envoyés que lorsque la série utilise le mode split fixe",
        seriesEndsBeforeAnchor:
          "La date de fin ne peut pas être antérieure à la date de début de la série",
        seriesEndedWithFutureEndsOn:
          "Pour raccourcir la série à l'avenir, modifiez uniquement la date de fin sans marquer la série comme terminée.",
      },
      financings: {
        notFound: "Financement introuvable",
        titleRequired: "Le titre est requis",
        installmentInvalid: "Le versement mensuel n'est pas valide",
        contractStartInvalid: "La date de début du contrat n'est pas valide",
        endsOnInvalid: "La date de fin n'est pas valide",
        endsBeforeContract: "La date de fin ne peut pas être antérieure au début du contrat",
        noFutureInstallments:
          "Il n’y a pas de versements futurs à partir d’aujourd’hui à ces dates. ",
        aiRequiresDocuments: "L'analyse de l'IA nécessite au moins un document",
        documentStorageKeyRequired: "Chaque document doit avoir une clé de stockage",
        summarizationFailed: "Impossible de générer le résumé du contrat avec l'IA",
        createFailed: "Impossible d'économiser le financement",
      },
      budgets: {
        yearMonthFormat: "annéeMois doit être AAAA-MM",
        plannedAmountInvalid: "planningAmountMinor n'est pas valide",
        categoryNotInHouse: "La catégorie {{categoryId}} n'appartient pas à ce ménage",
        categoryNotFound: "Catégorie introuvable",
      },
      vault: {
        notFound: "Article introuvable",
        nameRequired: "Le nom est requis",
        nameEmpty: "Le nom ne peut pas être vide",
        assetTypeInvalid:
          "Catégorie invalide. ",
      },
      receiptScans: {
        notFound: "Numérisation du reçu introuvable",
        imageKeyRequired: "imageStorageKey est requis",
        statusInvalid: "Statut invalide",
        dailyLimitExceeded:
          "Vous avez atteint la limite quotidienne d'analyse IA ({{limit}}). ",
        extractionFailed: "Impossible de lire le reçu. ",
        extractionDisabled:
          "L'extraction AI n'est pas configurée sur le serveur. ",
        imageTooLarge: "L'image est trop grande pour être traitée avec l'IA.",
        unsupportedMedia: "Format de fichier non pris en charge pour cette analyse.",
      },
      categories: {
        nameRequired: "Le nom est requis",
        notFound: "Catégorie introuvable",
        nameEmpty: "Le nom ne peut pas être vide",
      },
      dashboard: {
        yearMonthInvalid: "annéeMois n'est pas valide",
        yearMonthFormat: "annéeMois doit être AAAA-MM",
      },
      notifications: {
        notFound: "Notification introuvable",
        deviceNotFound: "Appareil introuvable",
      },
      storage: {
        fileRequired: "Un fichier est requis",
        mimeNotAllowed: "Type de fichier non autorisé : {{mime}}",
        fileTooLarge: "Fichier trop volumineux. ",
        keyRequired: "La clé de l'objet de stockage est requise",
        invalidKey: "Clé d'objet de stockage invalide ou non autorisée",
      },
      account: {
        userNotFound: "Utilisateur introuvable",
        deletionBlockedReferences:
          "La suppression du compte n'a pas pu être terminée en raison de références de données en attente. Contactez le support.",
      },
      publicContact: {
        sendFailed: "Impossible d'envoyer le message. Réessayez plus tard.",
        serviceUnavailable:
          "L'envoi de messages n'est pas disponible pour le moment (service de messagerie non configuré ou indisponible).",
      },
    },
  },
};

// Exportar también como default para next-intl
export default fr;
